'use client';

import {
  useState, useRef, useCallback, useEffect, useMemo,
} from 'react';
import { useRouter } from 'next/navigation';
import { useAppSelector, useAppDispatch } from '../../lib/hooks';
import {
  selectIsLoggedIn, selectToken, selectCredits, selectUserId, selectEmail,
  selectHasPurchasedCredits, selectCreditsExpireAt,
  updateUserData,
} from '../../lib/features/auth/authSlice';
import { getUserInfo } from '../../services/users.service';
import {
  generateWidget, refineWidget, createJobSSE, getJobStatus, downloadGeneratedFile,
  downloadGeneratedWidget, importOwnSvghmi,
  renderAiWidget, getAiQuota, getMyJobs, getJobPreview, getActiveJob, vectorizeImage,
  renderSvghmiContent, rateJob, getGeneratorStyles, chooseVariant, requestMoreVariants,
  restyleJob,
} from '../../services/ai-generator.service';
import { sendMetrics } from '../../services/ga.service';
import SvghmiPreview from '../converter/preview/SvghmiPreview';
import PricingBuyButton from '../components/PricingBuyButton';
import BuyNowLink from '../components/checkout/BuyNowLink';
import { useCheckoutPopup, buildCreditsCheckoutUrl } from '../components/checkout/useCheckoutPopup';
import { shouldAutoOpenCheckout } from './checkoutAutoopen';
import {
  AUTO_STYLE, ICON_STYLE, buildStyleCatalog, canAskMoreVariants, canRestyle, generateStyleParams,
  getVariants, isChoosable, isVariantsReady, variantsExpiresAt,
} from './variants';
import {
  PriceBadge, RestyleBar, Showcase, ShowcaseHero, StylePicker, VariantsGrid,
} from './GeneratorV2Parts';
import {
  SIGNUP_CREDITS, SIGNUP_CREDITS_DAYS, formatCredits, normalizeAiPricing, signupCreditsReach,
  signupOfferText,
} from '../../utils/aiPricing';
import {
  bestValueIndex, formatPricePerCredit, packTitle, sortPacks,
} from '../../utils/creditPacks';
import {
  PROGRESS_STAGES, groupJobLineages, isIconRequest,
  isTakingLonger, parseShowcaseManifest, promptHint, refineTariff, stageIndexOf, waitBarFraction,
  waitEstimate, waitExpectation, waitingPhraseAt, widgetFileName,
} from './generatorUi';
import { CoilmindBand, CoilmindCard, CoilmindPricingLine } from './CoilmindPitch';
import { EditChatPanel, useEditChat } from './EditChat';
import { editAllowanceLabel, suggestionChips } from './editChatState';
// eslint-disable-next-line import/extensions -- a JSON module needs its extension
import SHOWCASE_MANIFEST from '../../public/images/ai-generator/showcase/showcase.json';
import HOW_IT_WORKS from './howItWorks';
import FAQ from './faq';
import './ai-generator.css';

// Why a widget got a thumbs down. Short, clickable, and entirely optional — a
// tag gets answered, a required essay does not. "Other…" opens a one-line box
// for the complaints these four do not cover.
const RATING_REASONS = [
  { key: 'not_asked', label: 'Not what I asked for' },
  { key: 'crude', label: 'Looks crude' },
  { key: 'logic', label: 'Logic does not work' },
  { key: 'other', label: 'Other…' },
];

/**
 * Thumb glyph for the rating buttons, drawn rather than typed.
 *
 * The emoji it replaces brought its own palette and its own vertical metrics
 * into a panel built out of two greys and one blue, so it read as pasted on
 * from somewhere else. This inherits `currentColor`, which is what lets the
 * button go green or amber as a whole once it is pressed.
 */
function ThumbIcon({ down = false }) {
  return (
    <svg
      className="ag-rate-icon"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
      style={down ? { transform: 'rotate(180deg)' } : undefined}
    >
      <path
        fill="currentColor"
        d="M5.6 14.5H3.3a1.3 1.3 0 0 1-1.3-1.3V7.9a1.3 1.3 0 0 1 1.3-1.3h2.3zM6.9 6.6l2.7-5A1.6 1.6 0 0 1 12 2.9l-.6 3.1h2.4a1.6 1.6 0 0 1 1.6 1.9l-.9 4.9a1.6 1.6 0 0 1-1.6 1.3H6.9z"
      />
    </svg>
  );
}

// Typewriter placeholder — cycles real example prompts (gated behind
// reduced-motion). It replaced the example chips under the prompt box (owner,
// 2026-10-12: nobody understood they fill the prompt; the showcase tiles do).
const PLACEHOLDER_PROMPTS = [
  'Centrifugal pump with electric motor on a base frame, inlet and outlet nozzles…',
  'Create a circular gauge, 0–10 bar, green-amber-red zones, needle and centre value…',
  'Stainless mixing tank with top-mounted agitator motor, level and temperature…',
  'A vertical tank level indicator with high/low alarm markers and a % label…',
  'Roots blower with silencer and drive motor…',
  'Compact status LED — Running / Stopped / Fault — with a glow and runtime hours…',
];

// What a credit pack buys on this page (the converter's features live on /pricing).
const planFeatures = (pricing, tier) => {
  const perWidget = pricing.generation > 0 ? Math.floor(tier.credits / pricing.generation) : null;
  return [
    perWidget ? `About ${perWidget} AI widgets` : 'AI widgets',
    'Credits never expire',
    'Also work in the SVG → SVGHMI converter',
  ];
};

/**
 * The generator's tariff as rows, from GET /quota → pricing (normalised).
 * Nothing hard-coded beyond normalizeAiPricing's fallbacks and the sign-up
 * credit grant (the generator has no separate free trial since 2026-10-12).
 */
const buildTariffRows = (pricing) => [
  {
    what: 'Sign-up credits',
    cost: `${SIGNUP_CREDITS} free credits`,
    note: `New account, no card, valid ${SIGNUP_CREDITS_DAYS} days — ${signupCreditsReach(pricing.generation)}`,
  },
  {
    what: 'Widget or equipment drawing',
    cost: formatCredits(pricing.generation),
    note: pricing.variants_count > 1
      ? `Equipment: ${pricing.variants_count} variants to pick from, ${formatCredits(pricing.variants)} for all`
      : null,
  },
  { what: 'Icon', cost: formatCredits(pricing.icon), note: null },
  {
    what: 'Edits',
    cost: `${pricing.edit_included_generated} included with every generated widget`,
    note: `Then ${formatCredits(pricing.edit_pack_price)} per ${pricing.edit_pack_size} edits; questions free`,
  },
  { what: 'Restyle', cost: formatCredits(pricing.restyle), note: null },
  { what: 'More variants', cost: formatCredits(pricing.more_variants), note: 'Once per widget' },
  {
    what: 'Your own SVGHMI',
    cost: 'Free to open',
    note: `First change ${formatCredits(pricing.edit_pack_price)}, opens ${pricing.edit_pack_size} edits`,
  },
];

// Creativity levels of a generation (the request's `creativity`), with the
// one-line hint shown as each option's tooltip.
const CREATIVITY_LEVELS = [
  {
    key: 'low', label: 'Precise', color: 'var(--ag-ok)', hint: 'Sticks closely to your description',
  },
  {
    key: 'medium', label: 'Balanced', color: 'var(--ag-accent)', hint: 'Follows your description, fills in sensible details',
  },
  {
    key: 'high', label: 'Creative', color: 'var(--ag-warn)', hint: 'Takes more freedom with the design',
  },
];

// Cap of the auto-growing prompt box, px; past it the box scrolls.
const PROMPT_MAX_PX = 320;

const STATUS_LABELS = {
  queued: 'In queue...',
  pending: 'Starting...',
  generating: 'Generating widget...',
  validating: 'Validating SVGHMI...',
  fixing: 'Fixing validation errors...',
  rendering: 'Rendering preview...',
  reviewing: 'Checking the preview...',
  completed: 'Widget ready!',
  failed: 'Generation failed.',
  variants_ready: 'Variants ready — pick one.',
  awaiting_pick: 'Variants ready — pick one.',
  expired: 'These variants have expired.',
};

// ── Import ("vectorize") mode ────────────────────────────────────────────────
// Client-side upload guard — mirror the backend's accepted set + 9 MB ceiling so
// we reject obvious mistakes before spending a round-trip (and a credit).
const IMPORT_MAX_BYTES = 9 * 1024 * 1024;
const IMPORT_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml,.svg,.png,.jpg,.jpeg,.webp';
const IMPORT_ACCEPTED_TYPES = new Set([
  'image/png', 'image/jpeg', 'image/jpg', 'image/webp',
  'image/svg+xml', 'text/xml', 'application/xml',
]);
const IMPORT_ACCEPTED_EXT = /\.(png|jpe?g|webp|svg)$/i;

// Validate a picked file against type + size. Returns an error string, or null when ok.
function validateImportFile(file) {
  if (!file) return 'No file selected.';
  const typeOk = IMPORT_ACCEPTED_TYPES.has((file.type || '').toLowerCase())
    || IMPORT_ACCEPTED_EXT.test(file.name || '');
  if (!typeOk) return 'Unsupported file type. Upload a PNG, JPG, WEBP or SVG.';
  if (file.size > IMPORT_MAX_BYTES) {
    return `File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum is 9 MB.`;
  }
  return null;
}

// "Edit my SVGHMI" upload guard — mirrors POST /ai-generator/import (2 MB).
const OWN_MAX_BYTES = 2 * 1024 * 1024;
const OWN_ACCEPT = '.svghmi,image/svg+xml,.svg';

function validateOwnFile(file) {
  if (!file) return 'No file selected.';
  if (!/\.(svghmi|svg)$/i.test(file.name || '')) return 'Choose a .svghmi widget file.';
  if (file.size > OWN_MAX_BYTES) {
    return `File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). The limit is 2 MB.`;
  }
  return null;
}

// Caption under a revision thumbnail in the lineage strip.
const revisionTag = (rev, i) => {
  if (i === 0) return 'orig';
  return rev.kind === 'restyle' ? 'style' : `edit ${i}`;
};

const formatBytes = (b) => {
  if (b === null || b === undefined) return '—';
  if (b < 1024) return `${b} B`;
  return `${(b / 1024).toFixed(b < 10240 ? 1 : 0)} KB`;
};

const formatClock = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

// Shared progress readout — used both for the first generation (full-size, in the
// empty canvas) and for a refine (compact strip above the widget we already show).
// No percentage: the backend reports stages, not progress. The readout says which
// stage the job is in (Drawing → Validating → Rendering preview), with the server's
// own message, a real elapsed clock and an honest expectation ("usually about 3
// min, can take up to 10"). The soft bar is a feel for time against the typical
// duration and never fills on its own; past the typical time it turns into an
// indeterminate bar with a "still working" note instead of looking stuck.
function GenerationProgress({
  elapsedMs, status, statusMessage, queuePosition, isRetrying, compact, estimate,
}) {
  const current = stageIndexOf(status);
  const late = isTakingLonger(elapsedMs, estimate);
  return (
    <div className={`ag-gp${compact ? ' compact' : ''}`}>
      <div className="ag-gp-head">
        <span className="ag-gp-msg">{statusMessage || 'Starting…'}</span>
        <span className="ag-gp-nums ag-mono">
          <span className="ag-gp-clock" aria-label="Elapsed time">{formatClock(elapsedMs)}</span>
        </span>
      </div>
      <div className="ag-gp-track" aria-hidden="true">
        {late
          ? <div className="ag-gp-fill indet" />
          : <div className="ag-gp-fill" style={{ width: `${Math.round(waitBarFraction(elapsedMs, estimate) * 100)}%` }} />}
      </div>
      <div className={`ag-gp-eta${late ? ' is-late' : ''}`} aria-live="polite">
        {late ? 'Taking a bit longer than usual — still working.' : waitExpectation(estimate)}
      </div>
      <ol className="ag-stages" aria-label="Generation stages">
        {PROGRESS_STAGES.map((st, i) => {
          let state = 'todo';
          if (i < current) state = 'done';
          else if (i === current) state = 'on';
          return (
            <li key={st.key} className={`is-${state}`} aria-current={state === 'on' ? 'step' : undefined}>
              <span className="ag-stage-dot" aria-hidden="true" />
              {st.label}
            </li>
          );
        })}
      </ol>
      {queuePosition > 0 && status === 'queued' && (
        <div className="ag-gp-sub">
          Your request is #
          {queuePosition}
          {' '}
          in the queue — it starts automatically.
        </div>
      )}
      {isRetrying && (
        <div className="ag-gp-sub">
          The server is under heavy load — your widget will still appear here.
        </div>
      )}
      {/* Something friendly to read while it works; the stages above are
          the real progress. Keyed so each new line fades in. */}
      <div key={waitingPhraseAt(elapsedMs)} className="ag-gp-sub ag-gp-cheer" aria-live="off">
        {waitingPhraseAt(elapsedMs)}
      </div>
      {/* True: the job runs on the server; on return the page resumes it
          (GET /active-job) and finished widgets are listed in History. */}
      {!compact && (
        <div className="ag-gp-sub ag-gp-leave">
          You can leave this page or switch tabs — the job keeps running on our
          server, and when you come back the widget will be here and in History.
        </div>
      )}
    </div>
  );
}

// History card thumbnail: the server render is fetched once the card scrolls
// near the viewport (IntersectionObserver), not on hover — touch screens never
// hover, and a grid of "Hover to preview" told nobody anything.
function HistoryThumb({ jobId, preview, onVisible }) {
  const ref = useRef(null);
  useEffect(() => {
    if (preview) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      onVisible(jobId);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        onVisible(jobId);
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [jobId, preview, onVisible]);

  let body = null;
  if (!preview || preview === 'loading') {
    body = <div className="spinner-border spinner-border-sm text-secondary" role="status" />;
  } else if (preview === 'error') {
    body = <span className="ag-mono" style={{ fontSize: 12, color: 'var(--ag-faint)' }}>Preview unavailable</span>;
  } else {
    // Server render of the user's own widget, same as the revision strip.
    body = <div dangerouslySetInnerHTML={{ __html: preview }} />;
  }
  return <div ref={ref} className="ag-hist-thumb-in">{body}</div>;
}

// "How to add it to WinCC Unified" under the download button. The steps are
// the owner's library manual ("SVGHMI files", "Troubleshooting",
// "Limitations"), adapted to a single downloaded file.
function WinccGuide({ guideRef }) {
  return (
    <details className="ag-guide" ref={guideRef} id="wincc-guide">
      <summary>How to add it to WinCC Unified</summary>
      <div className="ag-guide-body">
        <ol className="ag-guide-steps">
          <li>
            Copy the downloaded .svghmi file into
            {' '}
            <code>YOUR_PROJECT_FOLDER\UserFiles\SvgControls</code>
            . If the
            {' '}
            <code>SvgControls</code>
            {' '}
            folder does not exist, create it.
          </li>
          <li>Open your TIA Portal project.</li>
          <li>Open any screen.</li>
          <li>
            In the
            {' '}
            <em>Dynamic widgets</em>
            {' '}
            section, press
            {' '}
            <strong>Refreshes the outdated dynamic graphics</strong>
            .
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className="ag-guide-shot"
              src="/images/ai-generator/tia-dynamic-widgets.png"
              alt="TIA Portal toolbox: the Dynamic widgets section with the refresh button"
              width={294}
              height={417}
              loading="lazy"
            />
          </li>
          <li>The widget appears in the Dynamic widgets section.</li>
        </ol>
        <p className="ag-guide-h">If something does not show up</p>
        <ul className="ag-guide-notes">
          <li>
            Changed a widget file? Close all screens in TIA Portal, refresh the dynamic
            widgets, then open the screens again.
          </li>
          <li>
            For Runtime, run
            {' '}
            <strong>Compile → Rebuild All</strong>
            : a delta load does not update widgets.
          </li>
          <li>
            The file name and the
            {' '}
            <code>name</code>
            {' '}
            /
            {' '}
            <code>displayName</code>
            {' '}
            in
            {' '}
            <code>&lt;hmi:self&gt;</code>
            {' '}
            must be unique among your widgets. Every generated widget gets its own
            unique type name (for example AiWidget_Pump_3f2a1c), and the downloaded
            file is named after it — keep the files as they are.
          </li>
          <li>
            Animation and the hover effect work only in WinCC Unified PC RT, not on
            Unified Basic or Comfort panels and not in the TIA Portal editor.
            Everything driven by a property value works on panels too.
          </li>
        </ul>
        <p className="ag-guide-more">
          <a href="#faq-what">More in the FAQ</a>
          {' · Stuck? '}
          <a href="mailto:support@svghmi.pro">support@svghmi.pro</a>
          {' — a human answers.'}
        </p>
      </div>
    </details>
  );
}

export default function AiGenerator({ pricingTiers = [] }) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const token = useAppSelector(selectToken);
  const credits = useAppSelector(selectCredits);
  const userId = useAppSelector(selectUserId);
  const email = useAppSelector(selectEmail);
  const hasPurchasedCredits = useAppSelector(selectHasPurchasedCredits);
  const creditsExpireAt = useAppSelector(selectCreditsExpireAt);

  // Public credit packs for the pricing block — however many the API returns.
  const packs = useMemo(() => sortPacks(pricingTiers), [pricingTiers]);
  const bestPackIndex = bestValueIndex(packs);
  // 4 packs in one row on desktop, 2×2 on a tablet, stacked on a phone.
  const packColClass = packs.length >= 4 ? 'col-12 col-md-6 col-lg-3' : 'col-12 col-md-4';

  // Same "buy credits without leaving the page" popup the converter has used
  // for a while (app/converter/Controls.js), extracted to
  // app/components/checkout so both pages share the one mechanism.
  // `source: 'generator'` rides along on the show_checkout_popup GA4 event —
  // see the CHANGELOG for the exact query that separates generator-started
  // purchase intent from the converter's.
  const checkoutUrl = useMemo(() => buildCreditsCheckoutUrl(email), [email]);
  const { showCheckout } = useCheckoutPopup({ checkoutUrl, userId, source: 'generator' });

  // Hydration gate — auth state comes from redux-persist (localStorage), which is empty
  // during SSR and the first client paint. Rendering any auth/credits/quota/history-driven
  // branch before rehydration causes a server/client HTML mismatch (Next.js hydration error).
  // We keep `hydrated` false until after mount so the server and first client render are
  // identical (the neutral, logged-out shell); real auth state swaps in on the next paint.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => { setHydrated(true); }, []);

  // Auth-derived flags, gated so they read as "logged-out / free tier" until hydrated.
  const authReady = hydrated && isLoggedIn;

  // Generator mode: "describe" (text-to-widget) | "import" (raster/SVG → SVGHMI).
  const [mode, setMode] = useState('describe');

  const [prompt, setPrompt] = useState('');
  // GET /quota: { pricing, credits, credits_expire_at, trial_blocked, ... }
  const [quota, setQuota] = useState(null);
  // 104 users opened the generator tab and 4 ever pressed Generate, with no event in
  // between to say where the other 100 stopped. These three refs keep each funnel
  // step to one hit per visit so the drop-off can be read without inflating counts.
  const promptFocusSentRef = useRef(false);
  const promptTypedSentRef = useRef(false);
  const blockedReasonSentRef = useRef(null);
  // Everyone pays credits (2026-10-12: the separate free trial is gone; a new
  // account spends its sign-up credits here like in the converter). The one
  // account-level block left is a non-purchaser the server flags as abusive
  // (disposable email): GET /quota → trial_blocked, server answer
  // PURCHASE_REQUIRED.
  const accountBlocked = !hasPurchasedCredits && !!quota?.trial_blocked;
  // Unpurchased sign-up credits expire (the server zeroes them on the next
  // request); `null` = balance unknown → the gate allows, the server decides.
  const signupExpiresMs = !hasPurchasedCredits && creditsExpireAt ? creditsExpireAt * 1000 : null;
  const signupExpired = signupExpiresMs !== null && signupExpiresMs <= Date.now();
  let balance = null;
  if (typeof credits === 'number') balance = signupExpired ? 0 : credits;
  const signupExpiryLabel = signupExpiresMs !== null && !signupExpired && balance > 0
    ? new Date(signupExpiresMs).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  // Prices come from the server (GET /quota → pricing); defaults until it answers.
  const pricing = useMemo(() => normalizeAiPricing(quota?.pricing), [quota]);
  const freeRefines = pricing.refine_free_per_widget;
  const tariffRows = buildTariffRows(pricing);

  // Import-mode state (kept separate from the describe/generate/refine flow).
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null); // object URL for the thumbnail
  const [importConverting, setImportConverting] = useState(false);
  const [importResult, setImportResult] = useState(null); // vectorize response
  const [importError, setImportError] = useState(null);
  // The import error is about credits: show a Buy credits link next to it.
  const [importErrorBuy, setImportErrorBuy] = useState(false);
  useEffect(() => { if (!importError) setImportErrorBuy(false); }, [importError]);
  const [importDragOver, setImportDragOver] = useState(false);
  const [importElapsedMs, setImportElapsedMs] = useState(0);
  const importInputRef = useRef(null);
  // "Edit my SVGHMI": the user's own .svghmi opened as a job (POST /import).
  const [ownFile, setOwnFile] = useState(null);
  const [ownDescription, setOwnDescription] = useState('');
  const [ownBusy, setOwnBusy] = useState(false);
  const [ownError, setOwnError] = useState(null);
  const [ownDragOver, setOwnDragOver] = useState(false);
  const ownInputRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  // What the job in flight is: 'generate' (the Generate button), 'refine' (an
  // edit), or 'follow' (a picked variant, restyle…). Only 'generate' may say
  // "Generating…" on the Generate button; an edit shows its progress at the
  // edit field.
  const [busyKind, setBusyKind] = useState(null);
  const [pendingInstruction, setPendingInstruction] = useState('');
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [queuePosition, setQueuePosition] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [svgPreview, setSvgPreview] = useState(null);
  const [renderMetadata, setRenderMetadata] = useState(null);
  const [sourceXml, setSourceXml] = useState(null);
  const [creativity, setCreativity] = useState('medium'); // low, medium, high
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPreviews, setHistoryPreviews] = useState({}); // jobId -> svgString

  // Refine (edit-mode) lineage — ordered chain [generate, refine1, refine2, ...]
  const [revisions, setRevisions] = useState([]);
  const [rootJobId, setRootJobId] = useState(null);
  const [refineInstruction, setRefineInstruction] = useState('');

  // The user's own verdict on the widget on screen. Keyed by job id, because a
  // revision strip switches the displayed job and a thumb pressed on "edit 2"
  // must not show up under "orig".
  const [ratings, setRatings] = useState({}); // jobId -> { rating, reason }
  const [ratingBusy, setRatingBusy] = useState(false);
  const [ratingError, setRatingError] = useState(null);
  const [ratingWhyOpen, setRatingWhyOpen] = useState(false);
  const [ratingOtherOpen, setRatingOtherOpen] = useState(false);
  const [ratingOtherText, setRatingOtherText] = useState('');

  // Style / detail picker (GET /styles). The catalogue starts from the local
  // fallback so the picker renders before (or without) the backend answer.
  const [styleCatalog, setStyleCatalog] = useState(() => buildStyleCatalog(null));
  const [styleId, setStyleId] = useState(AUTO_STYLE);
  const [detailId, setDetailId] = useState(null);
  // The Icon style card is the icon path (style 'icon'); everything else is
  // a widget or an equipment drawing, which the backend tells apart itself.
  const iconMode = styleId === ICON_STYLE;
  const genPrice = iconMode ? pricing.icon : pricing.generation;
  const generationPriceLabel = formatCredits(genPrice);
  const hasCreditsFor = (price) => balance === null || balance >= price;

  // A job parked in status variants_ready: { jobId, variants, expiresAt }.
  const [variantsJob, setVariantsJob] = useState(null);
  const [choosingIdx, setChoosingIdx] = useState(null);
  const [variantsError, setVariantsError] = useState(null);
  // Jobs whose free "more variants" were already used (409 MORE_USED / asked).
  const [moreUsed, setMoreUsed] = useState({});
  const [restyleBusy, setRestyleBusy] = useState(false);
  const [restyleError, setRestyleError] = useState(null);

  // Progress readout — start timestamp and the live elapsed clock.
  const [genStartedAt, setGenStartedAt] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  // What the wait estimate is based on: the kind of job in flight, and the
  // server's estimate_seconds { typical, max } once a status carries it.
  const [waitMode, setWaitMode] = useState('widget');
  const [serverEstimate, setServerEstimate] = useState(null);
  const estimate = useMemo(
    () => waitEstimate(waitMode, serverEstimate),
    [waitMode, serverEstimate],
  );

  const sseRef = useRef(null);
  const pollingRef = useRef(null);
  const historyRef = useRef(null);
  const composerRef = useRef(null);
  const promptRef = useRef(null);

  // "What you can make" showcase (public/images/ai-generator/showcase). No
  // manifest yet → [] and the empty state shows the one shipped sample.
  // The balance in redux is the one from login time; credits added since
  // (a purchase, an admin grant) would show as a stale number here. Refresh it
  // from the server on open, as the profile and the converter do.
  useEffect(() => {
    if (!isLoggedIn || !token) return undefined;
    let alive = true;
    getUserInfo(token)
      .then(({ user }) => { if (alive && user) dispatch(updateUserData(user)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [isLoggedIn, token, dispatch]);

  // Bundled at build time so the tiles are in the server HTML: fetching the
  // manifest after hydration made them appear seconds late.
  const showcase = useMemo(() => parseShowcaseManifest(SHOWCASE_MANIFEST), []);

  // The prompt box grows with its text (up to a cap, then it scrolls), like
  // the composers of image-generation services.
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, PROMPT_MAX_PX)}px`;
  }, [prompt, mode]);

  // Refs to keep lineage/ids fresh inside SSE/polling callbacks
  const jobIdRef = useRef(null); // job whose widget is CURRENTLY on screen
  const pendingJobIdRef = useRef(null); // job currently in flight (not yet displayed)
  const refineBaseRef = useRef(null); // non-null while a refine is in flight = base job id
  const revisionsRef = useRef([]);
  const rootJobIdRef = useRef(null);
  const lastPromptRef = useRef('');
  const lastInstructionRef = useRef('');
  // Armed by onGenerate ONLY when a non-purchaser's generate is about to
  // spend sign-up credits (never for a purchaser, never by onRefine). Read and
  // cleared on settle to detect the "this generation just used the last
  // sign-up credits" transition, the one auto-open trigger for the checkout
  // popup. See maybeAutoOpenCheckout.
  const spendingSignupRef = useRef(false);
  // What the request in flight is, beyond generate/refine: 'choose' (a
  // variant was picked), 'more' (extra variants), 'restyle', or 'open' (a
  // job reopened from history / a ?job= link). Read and cleared on settle.
  const followUpRef = useRef(null);
  // Credits the request in flight costs: { jobId, amount, applied }. Deducted
  // from the redux balance once, when the job settles (completed or
  // variants_ready), and given back on a refunded failure only if deducted.
  const chargeRef = useRef(null);

  // Refs to always have latest callbacks (avoids stale closures in SSE/polling)
  const handleStatusUpdateRef = useRef(null);
  const handleResultRef = useRef(null);
  const handleErrorRef = useRef(null);
  const handleVariantsRef = useRef(null);
  const openJobRef = useRef(null);

  // Clean up on unmount
  useEffect(() => () => {
    sseRef.current?.close();
    if (pollingRef.current) clearInterval(pollingRef.current);
  }, []);

  // Typewriter placeholder — only runs while the prompt box is empty and motion is allowed.
  useEffect(() => {
    if (prompt) return undefined;
    const reduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion:reduce)').matches;
    if (reduce) {
      setTypedPlaceholder(PLACEHOLDER_PROMPTS[0]);
      return undefined;
    }
    let pi = 0;
    let ci = 0;
    let del = false;
    let timer = null;
    const tick = () => {
      const p = PLACEHOLDER_PROMPTS[pi];
      ci += del ? -1 : 1;
      setTypedPlaceholder(p.slice(0, ci));
      if (!del && ci === p.length) { del = true; timer = setTimeout(tick, 1600); return; }
      if (del && ci === 0) { del = false; pi = (pi + 1) % PLACEHOLDER_PROMPTS.length; }
      timer = setTimeout(tick, del ? 18 : 40);
    };
    tick();
    return () => { if (timer) clearTimeout(timer); };
  }, [prompt]);

  // Elapsed clock — runs only while a job is in flight.
  useEffect(() => {
    if (!generating || !genStartedAt) return undefined;
    const tick = () => setElapsedMs(Date.now() - genStartedAt);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [generating, genStartedAt]);

  // Arm the clock for a job that is starting (or being resumed) now. `mode`
  // picks the fallback wait estimate until the server sends its own.
  const startProgress = useCallback((startedAtMs, mode = 'widget') => {
    const t0 = startedAtMs || Date.now();
    setGenStartedAt(t0);
    setElapsedMs(Date.now() - t0);
    setWaitMode(mode);
    setServerEstimate(null);
  }, []);

  // Mirror lineage state into refs (read by SSE/polling callbacks without stale closures)
  useEffect(() => { jobIdRef.current = jobId; }, [jobId]);
  useEffect(() => { revisionsRef.current = revisions; }, [revisions]);
  useEffect(() => { rootJobIdRef.current = rootJobId; }, [rootJobId]);

  // Fetch quota — prices, and whether this account is blocked from the
  // generator until a purchase (see canGenerate/blockedReason below).
  useEffect(() => {
    if (isLoggedIn && token) {
      getAiQuota(token).then((data) => setQuota(data)).catch(() => {});
    }
  }, [isLoggedIn, token]);

  // Fetch user's generation history
  useEffect(() => {
    if (!isLoggedIn || !token) return;
    setHistoryLoading(true);
    getMyJobs(token, 8)
      .then((jobs) => setHistory(jobs || []))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [isLoggedIn, token]);

  // Load preview for a history item (called once its card is near the viewport)
  const historyRequestedRef = useRef(new Set());
  const loadHistoryPreview = useCallback(async (jobId) => {
    if (!token || historyRequestedRef.current.has(jobId)) return;
    historyRequestedRef.current.add(jobId);
    setHistoryPreviews((prev) => ({ ...prev, [jobId]: 'loading' }));
    try {
      const data = await getJobPreview(jobId, token);
      setHistoryPreviews((prev) => ({ ...prev, [jobId]: data.rendered_svg || 'error' }));
    } catch {
      setHistoryPreviews((prev) => ({ ...prev, [jobId]: 'error' }));
    }
  }, [token]);

  const downloadHistoryItem = useCallback(async (fileUrl, jobId, jobPrompt) => {
    try {
      // The server's name (the widget's unique WinCC type name); the
      // prompt-based name only for a file that carries none.
      const { blob, fileName } = await downloadGeneratedWidget(fileUrl, token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || widgetFileName(jobPrompt, jobId);
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError({ message: 'Download failed. Please try again.' });
    }
  }, [token]);

  const [isRetrying, setIsRetrying] = useState(false);

  const handleStatusUpdate = useCallback((data) => {
    setStatus(data.status);
    // The review step's server text ("Visual review via AI vision") is
    // internal jargon; everything else the server says is shown as-is.
    setStatusMessage((data.status === 'reviewing' && STATUS_LABELS.reviewing)
      || data.progress_message || STATUS_LABELS[data.status] || data.status);
    setIsRetrying(!!data.retry);
    if (data.estimate_seconds) setServerEstimate(data.estimate_seconds);
    if (data.queue_position !== undefined) {
      setQueuePosition(data.queue_position);
    }
    // Handle failed status received via status event (safety net)
    if (data.status === 'failed') {
      setError({ message: data.error_message || data.progress_message || 'Generation failed.' });
      setGenerating(false);
      setIsRetrying(false);
    }
  }, []);
  handleStatusUpdateRef.current = handleStatusUpdate;

  const resultReceivedRef = useRef(false);

  // localStorage key so the auto-opened popup fires at most once per visitor
  // per account on this browser — a device-level guard, not a durable one (a
  // different browser or a cleared profile sees it again; no migration was
  // added just for this, see the CHANGELOG).
  const AUTOOPEN_STORAGE_KEY = `ag_checkout_autoopen_shown_${userId || 'anon'}`;

  // Opens the checkout popup on the ONE auto-open transition — the decision
  // itself is the pure shouldAutoOpenCheckout above the component (unit
  // tested); this just adds the one-shot localStorage guard and the actual
  // popup call once that decision is already "yes".
  const maybeAutoOpenCheckout = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      if (window.localStorage.getItem(AUTOOPEN_STORAGE_KEY)) return;
      window.localStorage.setItem(AUTOOPEN_STORAGE_KEY, '1');
    } catch {
      // Private mode / storage disabled — show it anyway; worst case it can
      // repeat once for that visitor instead of being suppressed forever.
    }
    showCheckout(undefined, { trigger: 'auto' });
  }, [AUTOOPEN_STORAGE_KEY, showCheckout]);

  // Deduct what the settled request cost from the redux balance — once. The
  // amount is the server's own `credits_charged` from the start response when
  // it sent one, else the tariff from /quota (see the request handlers). The
  // same for every account: sign-up credits are credits too.
  const applyCharge = useCallback((settledJobId) => {
    const charge = chargeRef.current;
    if (!charge || charge.applied) return;
    if (charge.jobId && settledJobId && charge.jobId !== settledJobId) return;
    charge.applied = true;
    if (credits === null || !(charge.amount > 0)) return;
    dispatch(updateUserData({ credits: Math.max(0, credits - charge.amount) }));
  }, [credits, dispatch]);

  // Refresh quota and history after a real job settled (completed or
  // variants_ready).
  //
  // This is also the ONLY place that can trigger the checkout popup's
  // auto-open: only when THIS generate armed spendingSignupRef (a
  // non-purchaser spending sign-up credits — never a refine) AND the
  // freshly-fetched quota reports too few credits for another generation.
  // Reading the transition here, off the real request/response pair, keeps a
  // page reload of an already-empty account from popping it.
  const afterJobSettled = useCallback((isRefine) => {
    const wasSpendingSignupCredits = spendingSignupRef.current && !isRefine;
    spendingSignupRef.current = false;
    if (!token) return;
    getAiQuota(token).then((d) => {
      setQuota(d);
      if (shouldAutoOpenCheckout({ wasSpendingSignupCredits, quota: d })) {
        maybeAutoOpenCheckout();
      }
    }).catch(() => {});
    getMyJobs(token, 8).then((jobs) => setHistory(jobs || [])).catch(() => {});
  }, [token, maybeAutoOpenCheckout]);

  const handleResult = useCallback(async (data) => {
    // Guard against double invocation (SSE + polling race)
    if (resultReceivedRef.current) return;
    resultReceivedRef.current = true;

    const isRefine = !!refineBaseRef.current;
    const followUp = followUpRef.current;
    followUpRef.current = null;
    let kind = followUp?.kind || 'generate';
    if (isRefine) kind = 'refine';
    // Job-level fields (pipeline, style, detail…) sit next to `result` in a
    // job payload; they are folded into the stored result so the restyle bar
    // and "more variants" can read them for whichever revision is on screen.
    // `variants` is deliberately not copied — on the concept path it is a flag.
    const base = data.result || data;
    const resultData = {
      ...base,
      pipeline: data.pipeline ?? base.pipeline ?? null,
      style: data.style ?? base.style ?? null,
      detail: data.detail ?? base.detail ?? null,
      more_used: data.more_used ?? base.more_used,
      more_available: data.more_available ?? base.more_available,
    };
    // A canned showcase widget the server handed back without running the
    // pipeline: nothing was spent, so nothing below may pretend it was.
    const isSample = data.is_sample === true || resultData.is_sample === true;
    const completedJobId = data.job_id || resultData.job_id
      || pendingJobIdRef.current || jobIdRef.current;

    setStatus('completed');
    let doneMessage = 'Widget ready!';
    if (isSample) doneMessage = 'Free demo';
    else if (kind === 'refine' || kind === 'chat') doneMessage = 'Widget updated!';
    else if (kind === 'restyle') doneMessage = 'Style applied!';
    else if (kind === 'import') doneMessage = 'Your widget is open';
    setStatusMessage(doneMessage);
    setResult(resultData);
    setVariantsJob(null);
    setVariantsError(null);
    setChoosingIdx(null);
    // A resumed job may already carry a verdict (GET /job/:id echoes it); an
    // SSE result never does. Seeding here keeps the thumb from resetting when
    // the page is reopened on a job the user already rated.
    if (data.user_rating === 1 || data.user_rating === -1) {
      setRatings((prev) => ({
        ...prev,
        [completedJobId]: { rating: data.user_rating, reason: data.user_rating_reason || null },
      }));
    }
    setGenerating(false);
    setIsRetrying(false);
    // The displayed revision is now the completed job. A refine deliberately leaves
    // `jobId` pointing at its base while in flight, so the preview keeps rendering the
    // widget you already have instead of remounting against a half-finished job.
    if (completedJobId) {
      setJobId(completedJobId);
      jobIdRef.current = completedJobId;
    }
    pendingJobIdRef.current = null;
    // A sample is not a generation: it gets its own event so the demo clicks
    // stay countable in GA4 without being written into the jobs table.
    const doneEvents = {
      generate: 'ai_generate_complete',
      refine: 'ai_refine_complete',
      restyle: 'ai_restyle_complete',
      choose: 'ai_variant_complete',
      more: 'ai_variant_complete',
      import: 'ai_import_svghmi_complete',
      chat: 'ai_edit_chat_revision',
    };
    const doneEvent = isSample ? 'ai_sample_shown' : doneEvents[kind];
    if (doneEvent) sendMetrics(doneEvent, { user_id: userId });

    // Update credits in redux (deducted on server). A sample costs nothing on
    // the server, so the balance must not move here.
    if (!isSample) {
      applyCharge(completedJobId);
      afterJobSettled(isRefine);
    }

    // Use rendered SVG from server for preview
    const svg = data.rendered_svg || resultData.rendered_svg;
    const fileUrl = resultData.file_url || data.file_url;

    let previewSvg = svg || null;
    if (svg) {
      setSvgPreview(svg);
    } else if (fileUrl && token) {
      // Fallback: download the file and extract SVG for preview
      try {
        const blob = await downloadGeneratedFile(fileUrl, token);
        const text = await blob.text();
        const svgMatch = text.match(/<svg[\s\S]*<\/svg>/);
        if (svgMatch) {
          const [full] = svgMatch;
          previewSvg = full;
          setSvgPreview(full);
        }
      } catch { /* preview not critical */ }
    }

    // Property controls follow THIS job: its own render_metadata, or none —
    // never the previous widget's (an icon used to show the gauge's Value,
    // AlarmLimit… because a missing field left the old metadata in place).
    const metadata = data.render_metadata || resultData.render_metadata || null;
    setRenderMetadata(metadata);
    setSourceXml(null);

    // Record this revision in the lineage. Backend returns parent_job_id/root_job_id on
    // job records per the refine contract; degrade gracefully if not present yet.
    // A restyle or a chat revision is a follow-up of the widget on screen,
    // like a refine.
    const isFollowUp = kind === 'refine' || kind === 'restyle' || kind === 'chat';
    const followBase = refineBaseRef.current || followUp?.baseJobId || null;
    const parentJobId = resultData.parent_job_id ?? data.parent_job_id
      ?? (isFollowUp ? followBase : null);
    const rootId = resultData.root_job_id ?? data.root_job_id
      ?? (isFollowUp ? (rootJobIdRef.current || followBase) : completedJobId);
    let label = followUp?.label || lastPromptRef.current || data.prompt || 'Generated widget';
    if (kind === 'refine') label = lastInstructionRef.current;
    // A lineage's first revision is 'import' when it is the user's own file
    // (its first edit is priced like a generation — refineTariff).
    let revisionKind = resultData.pipeline === 'import' ? 'import' : 'generate';
    if (isFollowUp) revisionKind = kind;
    const revision = {
      jobId: completedJobId,
      kind: revisionKind,
      label,
      svg: previewSvg,
      sourceXml: null,
      renderMetadata: metadata || null,
      result: resultData,
      parentJobId,
      rootJobId: rootId,
      createdAt: Date.now(),
    };
    if (isFollowUp) {
      // The same job twice (a chat revision re-delivered) replaces, never duplicates.
      setRevisions((prev) => (prev.some((r) => r.jobId === completedJobId)
        ? prev.map((r) => (r.jobId === completedJobId ? revision : r))
        : [...prev, revision]));
    } else {
      // A fresh generate (or a chosen variant) starts a new lineage
      setRevisions([revision]);
      setRootJobId(completedJobId);
    }
    refineBaseRef.current = null;

    // Fetch source XML for editor, then patch it into the stored revision
    if (fileUrl && token) {
      downloadGeneratedFile(fileUrl, token)
        .then((blob) => blob.text())
        .then((xml) => {
          setSourceXml(xml);
          setRevisions((prev) => prev.map(
            (r) => (r.jobId === completedJobId ? { ...r, sourceXml: xml } : r),
          ));
        })
        .catch(() => {});
    }
  }, [token, userId, applyCharge, afterJobSettled]);
  handleResultRef.current = handleResult;

  // A job parked for the user's choice (status variants_ready). The payload
  // may be a bare status event without the list — then the job is re-read.
  const handleVariantsReady = useCallback(async (data) => {
    if (resultReceivedRef.current) return;
    const vJobId = data.job_id || pendingJobIdRef.current || jobIdRef.current;
    let payload = data;
    if (getVariants(payload).length === 0 && vJobId && token) {
      try {
        payload = await getJobStatus(vJobId, token);
      } catch { /* handled below as "no variants" */ }
    }
    if (resultReceivedRef.current) return;
    resultReceivedRef.current = true;
    const variants = getVariants(payload);
    const isRefine = !!refineBaseRef.current;
    const reopened = followUpRef.current?.kind === 'open';
    followUpRef.current = null;
    refineBaseRef.current = null;
    pendingJobIdRef.current = null;
    setGenerating(false);
    setIsRetrying(false);

    if (variants.length === 0) {
      setStatus('failed');
      setError({ message: 'The variants could not be loaded. Find the job in your history and try again.' });
      return;
    }
    setStatus('variants_ready');
    setStatusMessage(STATUS_LABELS.variants_ready);
    setError(null);
    setVariantsError(null);
    setChoosingIdx(null);
    setVariantsJob({
      jobId: vJobId,
      status: payload.status || 'variants_ready',
      variants,
      expiresAt: variantsExpiresAt(payload),
    });
    if (reopened) return; // nothing new was spent or produced
    sendMetrics('ai_variants_ready', { user_id: userId, count: variants.length });
    applyCharge(vJobId);
    afterJobSettled(isRefine);
  }, [token, userId, applyCharge, afterJobSettled]);
  handleVariantsRef.current = handleVariantsReady;

  const handleError = useCallback((data) => {
    setStatus('failed');
    setStatusMessage(data.message || 'Generation failed.');
    setError(data);
    setGenerating(false);
    setIsRetrying(false);

    // The balance is only deducted when a job settles, so a refund needs a
    // UI correction only if that deduction already happened.
    const charge = chargeRef.current;
    if (data.credits_refunded && charge?.applied && charge.amount > 0 && credits !== null) {
      dispatch(updateUserData({ credits: credits + charge.amount }));
    }
    chargeRef.current = null;
    followUpRef.current = null;
    refineBaseRef.current = null;
    pendingJobIdRef.current = null;
  }, [credits, dispatch]);
  handleErrorRef.current = handleError;

  // Safety-net polling — always runs alongside SSE to catch missed events
  const startPolling = useCallback((jId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    const stop = () => {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      sseRef.current?.close();
    };
    pollingRef.current = setInterval(async () => {
      try {
        const data = await getJobStatus(jId, token);
        if (isVariantsReady(data)) {
          stop();
          handleVariantsRef.current?.(data);
          return;
        }
        handleStatusUpdateRef.current?.(data);
        if (data.status === 'completed') {
          stop();
          handleResultRef.current?.(data);
        } else if (data.status === 'failed') {
          stop();
          handleErrorRef.current?.(data.error || { message: data.error?.message || 'Generation failed' });
        } else if (data.status === 'expired') {
          stop();
          handleErrorRef.current?.({ message: STATUS_LABELS.expired });
        }
      } catch {
        // Keep polling on network error
      }
    }, 3000);
  }, [token]);

  // Follow a job in flight: polling as the safety net, SSE for fast updates.
  const watchJob = useCallback((jId) => {
    startPolling(jId);
    const sse = createJobSSE(jId, token);
    sseRef.current = sse;
    sse.onStatus = (d) => handleStatusUpdateRef.current?.(d);
    sse.onResult = (d) => {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      handleResultRef.current?.(d);
    };
    sse.onVariants = (d) => {
      if (!isVariantsReady(d)) return; // another parked state: polling decides
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      handleVariantsRef.current?.(d);
    };
    sse.onError = (err) => {
      // If SSE fails, polling is already running as fallback
      if (err.credits_refunded || err.message !== 'SSE connection failed') {
        handleErrorRef.current?.(err);
      }
    };
  }, [token, startPolling]);

  // Resume active generation after page reload
  const resumeJob = useCallback((activeJobId, startedAtMs) => {
    resultReceivedRef.current = false;
    pendingJobIdRef.current = activeJobId;
    setBusyKind('generate');
    setGenerating(true);
    startProgress(startedAtMs);
    watchJob(activeJobId);
  }, [watchJob, startProgress]);

  useEffect(() => {
    if (!isLoggedIn || !token) return;
    getActiveJob(token).then((data) => {
      if (data.active && data.job) {
        const { job } = data;
        setStatus(job.status);
        setStatusMessage(job.progress_message || STATUS_LABELS[job.status] || job.status);
        if (job.queue_position > 0) setQueuePosition(job.queue_position);

        if (job.status === 'completed') {
          handleResult(job);
        } else if (isVariantsReady(job)) {
          handleVariantsReady(job);
        } else if (job.status === 'failed') {
          handleError(job.error || { message: 'Generation failed' });
        } else {
          // created_at is unix seconds — keeps the elapsed clock honest across a reload.
          resumeJob(job.job_id, job.created_at ? job.created_at * 1000 : undefined);
          if (job.estimate_seconds) setServerEstimate(job.estimate_seconds);
        }
        return;
      }
      // No job in flight: a "choose" link from the profile (?job=<id>) opens
      // that job here — its variants grid, or the finished widget.
      const linked = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('job')
        : null;
      if (linked && /^[\w-]{8,64}$/.test(linked)) openJobRef.current?.(linked);
    }).catch(() => {});
  }, [isLoggedIn, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Style catalogue for the picker; the local fallback stays on any failure.
  useEffect(() => {
    if (!isLoggedIn || !token) return undefined;
    let alive = true;
    getGeneratorStyles(token)
      .then((data) => { if (alive) setStyleCatalog(buildStyleCatalog(data)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [isLoggedIn, token]);

  // Picking a style resets the detail to that style's default.
  const onPickStyle = (id) => {
    setStyleId(id);
    const style = styleCatalog.styles.find((st) => st.id === id);
    setDetailId(style ? style.defaultDetail : null);
  };

  // Reopen a job (history card, ?job= link): its variants grid when it is
  // still waiting for a choice, otherwise the finished widget. Free — nothing
  // is charged or counted again.
  const openJob = useCallback(async (openJobId, label) => {
    if (!token || generating) return;
    try {
      const data = await getJobStatus(openJobId, token);
      if (!isVariantsReady(data) && data.status !== 'completed') {
        if (data.status === 'expired') setError({ message: STATUS_LABELS.expired });
        return;
      }
      resultReceivedRef.current = false;
      chargeRef.current = null;
      refineBaseRef.current = null;
      followUpRef.current = { kind: 'open', label };
      if (label) lastPromptRef.current = label;
      pendingJobIdRef.current = openJobId;
      setMode('describe');
      setError(null);
      if (isVariantsReady(data)) await handleVariantsReady({ ...data, job_id: openJobId });
      else await handleResult({ ...data, job_id: openJobId });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError({ message: err.statusCode === 404 ? 'That widget could not be found.' : (err.message || 'Could not open the widget.') });
    }
  }, [token, generating, handleResult, handleVariantsReady]);
  openJobRef.current = openJob;

  // On a phone the result panel sits under the compose box: bring it into
  // view when a job starts so the progress (and then the widget) is seen.
  const resultPanelRef = useRef(null);
  const scrollResultIntoView = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    if (!window.matchMedia('(max-width: 991.98px)').matches) return;
    resultPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onGenerate = async () => {
    if (!prompt.trim()) return;

    if (!isLoggedIn) {
      // Beacon: this navigates away immediately.
      sendMetrics('ai_generate_blocked', { user_id: userId, reason: 'not_logged_in' }, { beacon: true });
      router.push(`/auth-login?from=${encodeURIComponent('/ai-generator')}`);
      return;
    }

    if (accountBlocked) {
      sendMetrics('ai_generate_blocked', { user_id: userId, reason: 'account_blocked' });
      return;
    }
    if (!hasCreditsFor(genPrice)) {
      sendMetrics('ai_generate_blocked', { user_id: userId, reason: 'insufficient_credits' });
      setError({ message: `Not enough credits. A generation costs ${formatCredits(genPrice)}.`, buy: true });
      return;
    }

    // Armed only on the non-purchaser path — see the ref's own comment and
    // maybeAutoOpenCheckout.
    spendingSignupRef.current = !hasPurchasedCredits;

    // Reset state
    resultReceivedRef.current = false;
    refineBaseRef.current = null; // this is a generate, not a refine
    followUpRef.current = null;
    chargeRef.current = null;
    lastPromptRef.current = prompt;
    setVariantsJob(null);
    setVariantsError(null);
    setBusyKind('generate');
    setGenerating(true);
    setStatus('pending');
    setStatusMessage('Starting...');
    setError(null);
    setQueuePosition(0);
    startProgress(undefined, iconMode || isIconRequest(prompt) ? 'icon' : 'widget');
    // Any widget already on screen stays there (dimmed) until the new one lands, so the
    // result panel never empties out mid-flight and the layout stays put.

    const styleParams = iconMode
      ? { style: ICON_STYLE }
      : generateStyleParams(styleCatalog, styleId, detailId);
    scrollResultIntoView();
    try {
      const data = await generateWidget(prompt, token, null, creativity, styleParams);
      if (data.estimate_seconds) setServerEstimate(data.estimate_seconds);
      pendingJobIdRef.current = data.job_id;
      // What this run costs: the server's own figure when it sends one (an
      // icon or a variants run may be priced differently), else the tariff.
      chargeRef.current = {
        jobId: data.job_id,
        amount: typeof data.credits_charged === 'number' ? data.credits_charged : genPrice,
        applied: false,
      };

      // An untouched example prompt comes back already finished: the server
      // ships a widget it generated once with this very pipeline instead of
      // charging two credits for a nine-minute rerun of the same request.
      // Nothing to stream or poll — paint it and stop.
      if (data.is_sample) {
        sendMetrics('ai_sample_start', { user_id: userId });
        await handleResultRef.current?.(data);
        return;
      }

      sendMetrics('ai_generate_start', {
        user_id: userId, creativity, style: styleParams.style || AUTO_STYLE,
      });

      if (data.queue_position > 0) {
        setQueuePosition(data.queue_position);
        setStatus('queued');
        setStatusMessage(`In queue, position ${data.queue_position}...`);
      }

      watchJob(data.job_id);
    } catch (err) {
      chargeRef.current = null;
      if (err.statusCode === 409 && err.active_job) {
        // Already have an active job — resume watching it
        const job = err.active_job;
        setStatus(job.status);
        setStatusMessage(job.progress_message || STATUS_LABELS[job.status] || job.status);
        if (job.queue_position > 0) setQueuePosition(job.queue_position);
        resumeJob(job.job_id, job.created_at ? job.created_at * 1000 : undefined);
        return;
      }
      spendingSignupRef.current = false;
      if (err.statusCode === 402) {
        setError({ message: `Not enough credits. A generation costs ${formatCredits(genPrice)}.`, buy: true });
      } else if (err.statusCode === 403) {
        setError({
          message: err.message || 'AI generation on this account is unlocked by a credit purchase.',
          buy: err.error === 'PURCHASE_REQUIRED',
        });
      } else if (err.statusCode === 429) {
        setError({ message: err.message || 'Please wait before generating again' });
      } else {
        setError({ message: err.message || 'Failed to start generation.' });
      }
      setGenerating(false);
      setStatus(null);
    }
  };

  // "Saved. Next: …" toast after a download, and the guide it points to.
  const [toast, setToast] = useState(null);
  const guideRef = useRef(null);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(t);
  }, [toast]);
  const openGuide = () => {
    setToast(null);
    const el = guideRef.current;
    if (!el) return;
    el.open = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const onDownload = async () => {
    if (!result?.file_url) return;
    try {
      const { blob, fileName } = await downloadGeneratedWidget(result.file_url, token);
      sendMetrics('ai_download_widget', { user_id: userId });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // The server's name: the widget's unique WinCC type name (e.g.
      // AiWidget_Pump_3f2a1c.svghmi), so two downloads never clash in
      // SvgControls. Only a file without one is named after the root prompt.
      const namePrompt = revisions[0]?.label || lastPromptRef.current || prompt;
      a.download = fileName || widgetFileName(namePrompt, jobId);
      a.click();
      URL.revokeObjectURL(url);
      setToast('saved');
    } catch {
      setError({ message: 'Download failed. Please try again.' });
    }
  };

  // ── Rating ─────────────────────────────────────────────────────────────────
  // One tap, reason optional, changeable afterwards. The verdict is stored
  // optimistically: on a control this small a spinner reads as breakage, and
  // the thumb turning on IS the acknowledgement.
  const submitRating = useCallback(async (rating, reason) => {
    const targetJobId = jobIdRef.current || jobId;
    if (!targetJobId || !token) return;
    const previous = ratings[targetJobId] || null;

    setRatingBusy(true);
    setRatingError(null);
    setRatings((prev) => ({ ...prev, [targetJobId]: { rating, reason: reason || null } }));
    try {
      await rateJob(targetJobId, rating, token, reason);
      sendMetrics('ai_rate_widget', { user_id: userId, rating });
    } catch (err) {
      // Put the previous verdict back — a thumb that lights up without being
      // recorded is worse than no thumb at all.
      setRatings((prev) => ({ ...prev, [targetJobId]: previous }));
      setRatingError(err.message || 'Could not save your rating. Try again.');
    } finally {
      setRatingBusy(false);
    }
  }, [jobId, ratings, token, userId]);

  const onRateUp = () => {
    setRatingWhyOpen(false);
    setRatingOtherOpen(false);
    submitRating(1);
  };

  // A bare thumbs down is already the signal; the reason picker is an offer,
  // never a gate, so the vote is sent before it opens.
  const onRateDown = () => {
    setRatingWhyOpen(true);
    setRatingOtherOpen(false);
    submitRating(-1);
  };

  const onPickReason = (item) => {
    if (item.key === 'other') {
      setRatingOtherOpen(true);
      return;
    }
    setRatingOtherOpen(false);
    submitRating(-1, item.label);
  };

  const onSendOtherReason = () => {
    const text = ratingOtherText.trim();
    if (!text) return;
    submitRating(-1, text);
    setRatingOtherOpen(false);
  };

  // "Another variant" — a plain generate off the same prompt. onGenerate keeps the
  // current widget visible while the new one builds, so nothing collapses here either.
  const onRegenerate = () => {
    setError(null);
    onGenerate();
  };

  const onReset = () => {
    sseRef.current?.close();
    if (pollingRef.current) clearInterval(pollingRef.current);
    setPrompt('');
    setGenerating(false);
    setJobId(null);
    setStatus(null);
    setStatusMessage('');
    setResult(null);
    setError(null);
    setSvgPreview(null);
    setRenderMetadata(null);
    setSourceXml(null);
    setQueuePosition(0);
    setRevisions([]);
    setRootJobId(null);
    setRefineInstruction('');
    setRatings({});
    setRatingError(null);
    setRatingWhyOpen(false);
    setRatingOtherOpen(false);
    setRatingOtherText('');
    setVariantsJob(null);
    setVariantsError(null);
    setChoosingIdx(null);
    setRestyleError(null);
    refineBaseRef.current = null;
    followUpRef.current = null;
    chargeRef.current = null;
    setGenStartedAt(null);
    setElapsedMs(0);
  };

  // ── Import mode ────────────────────────────────────────────────────────────
  // Vectorizing is one blocking request with no server-side stages, so we only run a
  // clock (the bar is indeterminate) — same readout shape as generation.
  useEffect(() => {
    if (!importConverting) return undefined;
    const t0 = Date.now();
    const id = setInterval(() => setImportElapsedMs(Date.now() - t0), 250);
    return () => clearInterval(id);
  }, [importConverting]);

  // Revoke the object URL when the preview changes or the component unmounts,
  // so we never leak blobs across repeated uploads.
  useEffect(() => () => {
    if (importPreview) URL.revokeObjectURL(importPreview);
  }, [importPreview]);

  const selectImportFile = useCallback((file) => {
    if (!file) return;
    const err = validateImportFile(file);
    if (err) {
      setImportError(err);
      return;
    }
    setImportError(null);
    setImportResult(null);
    setImportFile(file);
    // Build a thumbnail preview (works for raster and SVG alike via <img>).
    setImportPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  }, []);

  const onImportReset = useCallback(() => {
    setImportPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setImportFile(null);
    setImportResult(null);
    setImportError(null);
    setImportConverting(false);
    if (importInputRef.current) importInputRef.current.value = '';
  }, []);

  const onImportDrop = useCallback((e) => {
    e.preventDefault();
    setImportDragOver(false);
    if (importConverting) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) selectImportFile(file);
  }, [importConverting, selectImportFile]);

  // ── Edit my SVGHMI ──────────────────────────────────────────────────────
  const selectOwnFile = (file) => {
    setOwnError(null);
    if (!file) return;
    const problem = validateOwnFile(file);
    if (problem) {
      setOwnFile(null);
      setOwnError(problem);
      return;
    }
    setOwnFile(file);
  };

  // Free: the file becomes a completed job and lands in the result workspace,
  // where Edit and Restyle work on it like on a generated widget.
  const onOpenOwn = async () => {
    if (!ownFile || ownBusy || generating) return;
    if (!isLoggedIn) {
      router.push(`/auth-login?from=${encodeURIComponent('/ai-generator')}`);
      return;
    }
    setOwnBusy(true);
    setOwnError(null);
    sendMetrics('ai_import_svghmi_start', { user_id: userId });
    try {
      const description = ownDescription.trim();
      const data = await importOwnSvghmi(ownFile, description, token);
      const label = description || ownFile.name.replace(/\.svghmi$/i, '');
      resultReceivedRef.current = false;
      refineBaseRef.current = null;
      chargeRef.current = null;
      followUpRef.current = { kind: 'import', label };
      lastPromptRef.current = label;
      pendingJobIdRef.current = data.job_id;
      setVariantsJob(null);
      setError(null);
      await handleResult(data);
      setOwnFile(null);
      if (ownInputRef.current) ownInputRef.current.value = '';
      scrollResultIntoView();
    } catch (err) {
      const byCode = {
        LIBRARY_WIDGET: 'This is a widget from the SVGHMI.pro library. Library widgets cannot be edited '
          + 'with AI — open a widget of your own.',
        FILE_TOO_LARGE: 'The file is too large — the limit is 2 MB.',
      };
      let message = byCode[err.error] || err.message || 'Could not open the file.';
      if (err.statusCode === 401) message = 'Please sign in to open your widget.';
      if (err.statusCode === 429) message = err.message || 'Too many uploads, please wait a moment.';
      setOwnError(message);
    } finally {
      setOwnBusy(false);
    }
  };

  const onConvertImport = async () => {
    if (!importFile || importConverting) return;

    if (!isLoggedIn) {
      router.push(`/auth-login?from=${encodeURIComponent('/ai-generator')}`);
      return;
    }
    // Same gates as onGenerate: 1 credit for everyone (sign-up credits too),
    // and the account-level block for flagged non-purchasers.
    if (accountBlocked) {
      sendMetrics('ai_vectorize_blocked', { user_id: userId, reason: 'account_blocked' });
      return;
    }
    if (!hasCreditsFor(1)) {
      sendMetrics('ai_vectorize_blocked', { user_id: userId, reason: 'insufficient_credits' });
      setImportError('Not enough credits. Importing a widget costs 1 credit.');
      setImportErrorBuy(true);
      return;
    }

    setImportConverting(true);
    setImportError(null);
    setImportErrorBuy(false);
    setImportResult(null);
    setImportElapsedMs(0);
    sendMetrics('ai_vectorize_start', { user_id: userId });

    try {
      const data = await vectorizeImage(importFile, token);
      setImportResult(data);
      sendMetrics('ai_vectorize_complete', { user_id: userId, charged: data.charged });
      // The server reports the balance left after the up-front charge.
      if (data.charged === 'credit' && credits !== null && typeof data.creditsRemaining === 'number') {
        dispatch(updateUserData({ credits: Math.max(0, data.creditsRemaining) }));
      }
    } catch (err) {
      // 401 only — a 403 here is PURCHASE_REQUIRED (account blocked until a
      // purchase), not an auth failure, and must surface as importError like
      // any other gate rejection, not bounce the user to the login page.
      if (err.statusCode === 401) {
        router.push(`/auth-login?from=${encodeURIComponent('/ai-generator')}`);
        return;
      }
      setImportError(err.message || 'Failed to convert the image. Please try again.');
      setImportErrorBuy(err.statusCode === 402 || err.error === 'PURCHASE_REQUIRED');
    } finally {
      setImportConverting(false);
    }
  };

  const onImportDownload = () => {
    if (!importResult?.svghmi) return;
    const blob = new Blob([importResult.svghmi], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = importResult.fileName || 'widget.svghmi';
    a.click();
    URL.revokeObjectURL(url);
    sendMetrics('ai_vectorize_download', { user_id: userId });
  };

  // Refine (edit-mode): keep the current widget and apply a small change to it.
  // Creates a NEW job derived from the current one; polls exactly like generate.
  const onRefine = async () => {
    const instruction = refineInstruction.trim();
    if (!instruction || generating) return;

    if (!isLoggedIn) {
      router.push(`/auth-login?from=${encodeURIComponent('/ai-generator')}`);
      return;
    }

    const base = jobIdRef.current || jobId;
    if (!base) return;

    const tariff = refineTariff(revisions, {
      freeRefines, refinePrice: pricing.refine, generationPrice: pricing.generation,
    });
    const paidRefine = !tariff.free;
    if (paidRefine && credits !== null && credits < tariff.price) {
      setError({
        message: tariff.importFirst
          ? `Not enough credits. The first edit of your own widget costs ${formatCredits(tariff.price)}.`
          : `Not enough credits. Further edits cost ${formatCredits(pricing.refine)} each.`,
      });
      return;
    }

    resultReceivedRef.current = false;
    followUpRef.current = null;
    chargeRef.current = null;
    refineBaseRef.current = base;
    lastInstructionRef.current = instruction;
    setPendingInstruction(instruction);
    setBusyKind('refine');
    setGenerating(true);
    setStatus('pending');
    setStatusMessage('Applying your edit...');
    setError(null);
    setQueuePosition(0);
    startProgress(undefined, 'refine');
    // NOTE: `result`/`svgPreview`/`jobId` are deliberately left alone. A refine keeps the
    // current widget on screen (dimmed, with a progress strip) so the panel never
    // collapses back to the empty state and the page doesn't reflow under the user.

    try {
      const data = await refineWidget(base, instruction, token, creativity);
      if (data.estimate_seconds) setServerEstimate(data.estimate_seconds);
      pendingJobIdRef.current = data.job_id;
      let refineCost = paidRefine ? tariff.price : 0;
      if (typeof data.credits_charged === 'number') refineCost = data.credits_charged;
      chargeRef.current = { jobId: data.job_id, amount: refineCost, applied: false };
      sendMetrics('ai_refine_start', { user_id: userId, creativity });

      if (data.queue_position > 0) {
        setQueuePosition(data.queue_position);
        setStatus('queued');
        setStatusMessage(`In queue, position ${data.queue_position}...`);
      }

      watchJob(data.job_id);
      setRefineInstruction('');
    } catch (err) {
      refineBaseRef.current = null;
      if (err.statusCode === 409 && err.active_job) {
        const job = err.active_job;
        setStatus(job.status);
        setStatusMessage(job.progress_message || STATUS_LABELS[job.status] || job.status);
        if (job.queue_position > 0) setQueuePosition(job.queue_position);
        resumeJob(job.job_id, job.created_at ? job.created_at * 1000 : undefined);
        return;
      }
      if (err.statusCode === 402) {
        setError({
          message: `Not enough credits for this edit. Further edits cost ${formatCredits(pricing.refine)} each.`,
          buy: true,
        });
      } else if (err.statusCode === 403) {
        setError({ message: err.message || 'This action is not available.', buy: err.error === 'PURCHASE_REQUIRED' });
      } else if (err.statusCode === 404) {
        setError({ message: 'The base widget could not be found. Try generating a new one.' });
      } else if (err.statusCode === 429) {
        setError({ message: err.message || 'Please wait before editing again.' });
      } else {
        setError({ message: err.message || 'Failed to start the edit.' });
      }
      setGenerating(false);
      setStatus(null);
    }
  };

  // View a specific revision from the lineage
  const onSelectRevision = (rev) => {
    if (!rev) return;
    setJobId(rev.jobId);
    jobIdRef.current = rev.jobId;
    setResult(rev.result);
    setSvgPreview(rev.svg);
    setRenderMetadata(rev.renderMetadata || null);
    setSourceXml(rev.sourceXml || null);
    setStatus('completed');
    setError(null);
    // The verdict itself is kept per job in `ratings`; only the transient
    // pickers are closed so they don't follow the eye to another revision.
    setRatingWhyOpen(false);
    setRatingOtherOpen(false);
    setRatingError(null);
    setRestyleError(null);
  };

  // Start following a job created by a follow-up action (choose / more /
  // restyle) that did not come back finished in the response itself.
  const followInFlight = (followJobId, message, mode = 'widget', serverEta = null) => {
    resultReceivedRef.current = false;
    pendingJobIdRef.current = followJobId;
    setBusyKind('follow');
    setGenerating(true);
    setStatus('pending');
    setStatusMessage(message);
    setError(null);
    setQueuePosition(0);
    startProgress(undefined, mode);
    if (serverEta) setServerEstimate(serverEta);
    watchJob(followJobId);
  };

  // ── Variants ──────────────────────────────────────────────────────────────
  // Choosing a variant turns the parked job into a normal completed one: the
  // response is a regular job payload and goes through handleResult.
  const onChooseVariant = async (idx) => {
    const target = variantsJob;
    if (!target || choosingIdx !== null || generating || !token) return;
    setChoosingIdx(idx);
    setVariantsError(null);
    sendMetrics('ai_variant_choose', { user_id: userId, idx });
    try {
      const data = await chooseVariant(target.jobId, idx, token, target.status);
      const chosenJobId = data.job_id || target.jobId;
      // The variants were paid for when the job started; anything the
      // server charges for the choice itself is reported here.
      chargeRef.current = {
        jobId: chosenJobId,
        amount: typeof data.credits_charged === 'number' ? data.credits_charged : 0,
        applied: false,
      };
      followUpRef.current = { kind: 'choose' };
      if (data.status === 'completed') {
        resultReceivedRef.current = false;
        pendingJobIdRef.current = chosenJobId;
        await handleResult({ ...data, job_id: chosenJobId });
      } else {
        setVariantsJob(null);
        setChoosingIdx(null);
        followInFlight(chosenJobId, 'Building the chosen variant...');
      }
    } catch (err) {
      setChoosingIdx(null);
      if (err.statusCode === 409 || err.statusCode === 410) {
        // Already chosen (another tab) or expired: show whatever the job is now.
        try {
          const now = await getJobStatus(target.jobId, token);
          if (now.status === 'completed') {
            chargeRef.current = null;
            followUpRef.current = { kind: 'open' };
            resultReceivedRef.current = false;
            await handleResult({ ...now, job_id: target.jobId });
            return;
          }
        } catch { /* fall through to the message */ }
        setVariantsError(err.statusCode === 410 || err.error === 'EXPIRED'
          ? 'These variants have expired. Generate the widget again.'
          : (err.message || 'This variant can no longer be chosen.'));
        return;
      }
      setVariantsError(err.message || 'Could not choose this variant. Please try again.');
    }
  };

  // "Show more variants" on a finished old-path widget: free, once per job.
  // The job goes back to generating and lands in variants_ready.
  const onMoreVariants = async () => {
    const base = jobIdRef.current || jobId;
    if (!base || generating || !token) return;
    setError(null);
    setMoreUsed((prev) => ({ ...prev, [base]: true }));
    sendMetrics('ai_more_variants_start', { user_id: userId });
    try {
      const data = await requestMoreVariants(base, token);
      const moreJobId = data.job_id || base;
      chargeRef.current = {
        jobId: moreJobId,
        amount: typeof data.credits_charged === 'number' ? data.credits_charged : pricing.more_variants,
        applied: false,
      };
      followUpRef.current = { kind: 'more' };
      refineBaseRef.current = null;
      if (isVariantsReady(data) && getVariants(data).length > 0) {
        resultReceivedRef.current = false;
        await handleVariantsReady({ ...data, job_id: moreJobId });
        return;
      }
      followInFlight(moreJobId, 'Drawing more variants...', 'more', data.estimate_seconds);
    } catch (err) {
      chargeRef.current = null;
      followUpRef.current = null;
      if (err.statusCode === 409 && err.error === 'MORE_USED') return; // button stays hidden
      if (err.statusCode === 409 && err.active_job) {
        const job = err.active_job;
        resumeJob(job.job_id, job.created_at ? job.created_at * 1000 : undefined);
        return;
      }
      // Anything else: the offer may still be valid — let the user retry.
      setMoreUsed((prev) => ({ ...prev, [base]: false }));
      setError({ message: err.message || 'Could not draw more variants. Please try again.' });
    }
  };

  // ── Restyle (concept pipeline) ─────────────────────────────────────────────
  // The same drawing in another style / detail: a new free job, normally
  // finished in the response itself; otherwise it is followed like any job.
  const onRestyle = async ({ style, detail }) => {
    const base = jobIdRef.current || jobId;
    if (!base || generating || restyleBusy || !token) return;
    setRestyleBusy(true);
    setRestyleError(null);
    sendMetrics('ai_restyle_start', { user_id: userId, style: style || '', detail: detail || '' });
    const label = [
      style ? styleCatalog.styles.find((st) => st.id === style)?.label || style : null,
      detail ? styleCatalog.details.find((d) => d.id === detail)?.label || detail : null,
    ].filter(Boolean).join(' · ');
    try {
      const data = await restyleJob(base, style, detail, token);
      const newJobId = data.job_id;
      const fallbackCost = data.import_transfer ? pricing.generation : pricing.restyle;
      chargeRef.current = {
        jobId: newJobId,
        amount: typeof data.credits_charged === 'number' ? data.credits_charged : fallbackCost,
        applied: false,
      };
      followUpRef.current = { kind: 'restyle', baseJobId: base, label };
      if (data.status === 'completed') {
        resultReceivedRef.current = false;
        pendingJobIdRef.current = newJobId;
        await handleResult(data);
      } else if (newJobId && data.import_transfer) {
        // Own widget: redrawn from parts in the new style — a real generation.
        followInFlight(newJobId, 'Redrawing your widget in the new style...', 'equipment', data.estimate_seconds);
      } else if (newJobId) {
        followInFlight(newJobId, 'Applying the new style...', 'refine', data.estimate_seconds);
      }
    } catch (err) {
      chargeRef.current = null;
      followUpRef.current = null;
      if (err.statusCode === 429) {
        setRestyleError(err.retry_after
          ? `Too many restyles — try again in ${err.retry_after} s.`
          : 'Too many restyles, please wait a moment.');
      } else if (err.error === 'NOT_EQUIPMENT') {
        setRestyleError("This file isn't equipment the restyler can redraw — try an edit instead, "
          + 'e.g. “make it flat grey, ISA-101 style”.');
      } else if (err.statusCode === 402) {
        setRestyleError(`Not enough credits — restyling your own widget costs ${formatCredits(pricing.generation)}.`);
      } else {
        setRestyleError(err.message || 'Could not restyle the widget.');
      }
    } finally {
      setRestyleBusy(false);
    }
  };

  // Render function for SvghmiPreview property controls
  const aiRenderFn = useMemo(() => {
    if (!jobId || !token) return null;
    return async (mockValues) => renderAiWidget(jobId, mockValues, token);
  }, [jobId, token]);

  // Memoized so SvghmiPreview receives a STABLE array reference across renders
  // that don't actually change the metadata — an inline `renderMetadata ? [x] : []`
  // literal would rebuild on every AiGenerator render and re-arm SvghmiPreview's
  // init effect via identity, resetting any in-progress property-control edit
  // (SvghmiPreview also content-guards this independently as a second line of
  // defense, but a stable reference here avoids the needless effect re-run).
  const generateRenderMetadataArr = useMemo(
    () => (renderMetadata ? [renderMetadata] : []),
    [renderMetadata],
  );

  // ── Edit chat ──────────────────────────────────────────────────────────
  // The sidebar chat edits the widget on screen in steps. A turn's `revision`
  // lands here exactly like a completed refine (result, lineage, download);
  // the balance moves only on `done` with credits_charged > 0.
  const resultRef = useRef(null);
  const creditsRef = useRef(credits);
  useEffect(() => {
    resultRef.current = result;
    creditsRef.current = credits;
  }, [result, credits]);

  const applyChatRevision = useCallback(async (rev, userText) => {
    const base = jobIdRef.current;
    let payload = null;
    try {
      const job = await getJobStatus(rev.job_id, token);
      if (job && job.status === 'completed') payload = job;
    } catch { /* fall back to the revision event itself */ }
    if (!payload) {
      // Older fields (pipeline, style…) carry over; the preview and lineage
      // links must come from the new job, not the old one.
      const {
        rendered_svg: _svg, parent_job_id: _parent, job_id: _old, ...prev
      } = resultRef.current || {};
      payload = {
        ...prev,
        status: 'completed',
        file_url: rev.file_url || prev.file_url,
        png_url: rev.png_url || null,
        render_metadata: rev.render_metadata || null,
      };
    }
    resultReceivedRef.current = false;
    chargeRef.current = null;
    refineBaseRef.current = null;
    followUpRef.current = { kind: 'chat', baseJobId: base, label: userText };
    pendingJobIdRef.current = rev.job_id;
    await handleResultRef.current?.({ ...payload, job_id: rev.job_id });
  }, [token]);

  const onChatCharged = useCallback((charged) => {
    const { current } = creditsRef;
    if (charged > 0 && typeof current === 'number') {
      dispatch(updateUserData({ credits: Math.max(0, current - charged) }));
    }
    if (token) getAiQuota(token).then((d) => setQuota(d)).catch(() => {});
    if (charged > 0) sendMetrics('ai_edit_chat_charged', { user_id: userId, credits: charged });
  }, [token, dispatch, userId]);

  const lineageJobIds = useMemo(() => revisions.map((r) => r.jobId), [revisions]);
  const chat = useEditChat({
    token,
    jobId: result && !result.is_sample ? jobId : null,
    knownJobIds: lineageJobIds,
    enabled: authReady && !!result && !result.is_sample,
    onRevision: applyChatRevision,
    onCharged: onChatCharged,
  });
  const chatOn = chat.available !== false;
  const chatPrice = editAllowanceLabel(chat.state.allowance, pricing, {
    imported: revisions[0]?.kind === 'import' || result?.pipeline === 'import',
  });
  const chatChips = useMemo(() => suggestionChips(renderMetadata?.params), [renderMetadata]);

  const onSendChat = async () => {
    const out = await chat.send();
    // No edit-chat endpoint on this backend: the message moves to the
    // one-shot refine box that replaces the chat.
    if (out?.fallback) setRefineInstruction(out.text);
    else if (out?.sent) sendMetrics('ai_edit_chat_send', { user_id: userId });
  };

  // "Restore this version": a revision of this lineage is switched to in
  // place; anything else is reopened like a history card.
  const onRestoreChatVersion = (restoreJobId) => {
    const rev = revisions.find((r) => r.jobId === restoreJobId);
    if (rev) onSelectRevision(rev);
    else openJob(restoreJobId);
  };

  // Everyone needs credits >= the price (unknown balance → allowed, the
  // server decides); a flagged non-purchaser is blocked until a purchase.
  const canGenerate = prompt.trim().length > 0 && !generating && !chat.busy && isLoggedIn
    && !accountBlocked && hasCreditsFor(genPrice);

  // Why the Generate button is unreachable, for someone who has written a prompt.
  // Order matters: the first unmet gate is the one the user actually sees.
  const blockedReason = (() => {
    if (!isLoggedIn) return 'not_logged_in';
    if (accountBlocked) return 'account_blocked';
    if (!hasCreditsFor(genPrice)) return 'insufficient_credits';
    return null;
  })();

  // The widget on screen is the shipped example, not something generated for
  // this user. It carries no job row, so rating and refine have nothing to talk
  // to — and, more to the point, it would be dishonest to offer "was this
  // right?" about a widget the user did not actually commission.
  const isSampleResult = !!result?.is_sample;

  const onPromptFocus = () => {
    if (promptFocusSentRef.current) return;
    promptFocusSentRef.current = true;
    sendMetrics('ai_prompt_focus', { user_id: userId });
  };

  // Debounced so prompt_length is the length the user settled on, not 1 character.
  useEffect(() => {
    const trimmed = prompt.trim();
    if (!trimmed || promptTypedSentRef.current) return undefined;
    const t = setTimeout(() => {
      promptTypedSentRef.current = true;
      sendMetrics('ai_prompt_typed', { user_id: userId, prompt_length: trimmed.length });
    }, 1500);
    return () => clearTimeout(t);
  }, [prompt, userId]);

  // The button is disabled rather than clickable-then-rejected, so the block has to
  // be reported from state. Once per distinct reason, not once per render.
  useEffect(() => {
    // Wait for redux-persist: mid-rehydration every user looks logged out.
    if (!hydrated) return;
    if (!prompt.trim() || !blockedReason || generating) return;
    if (blockedReasonSentRef.current === blockedReason) return;
    blockedReasonSentRef.current = blockedReason;
    sendMetrics('ai_generate_blocked', { user_id: userId, reason: blockedReason });
  }, [prompt, blockedReason, generating, hydrated, userId]);

  // Refine tariff for the current lineage: the first N completed refines are
  // free, then pricing.refine each (both from GET /quota → pricing).
  // (A lineage started from the user's own file pays its first edit like a
  // generation — see refineTariff.)
  const nextRefine = refineTariff(revisions, {
    freeRefines, refinePrice: pricing.refine, generationPrice: pricing.generation,
  });
  const freeEditsLeft = nextRefine.freeLeft;
  const nextRefineFree = nextRefine.free;
  const canRefine = isLoggedIn && !generating && refineInstruction.trim().length > 0
    && hasCreditsFor(nextRefine.price);

  // Follow-ups offered for the widget on screen.
  const currentRevision = revisions.find((r) => r.jobId === jobId) || null;
  const showMoreVariants = !!result && !generating && !variantsJob
    && currentRevision?.kind === 'generate'
    && !moreUsed[jobId] && canAskMoreVariants(result);
  const extraVariants = Math.max(1, pricing.variants_count - 1);
  // The user's own imported widget can be restyled too: it is redrawn from
  // parts as a queued job, paid like a generation (backend import transfer).
  const restyleIsImport = !!result && !result.is_sample && result.pipeline === 'import';
  const showRestyle = !!result && !variantsJob && (canRestyle(result) || restyleIsImport);
  const showVariants = !!variantsJob && !generating;

  // "7 properties to bind in WinCC: Value (number), AlarmLimit (number), …" —
  // what the engineer will see in TIA Portal, instead of a viewBox.
  const bindableLine = useMemo(() => {
    const params = (renderMetadata?.params || []).filter((p) => p && p.name);
    if (params.length === 0) return 'No properties to bind — a static graphic';
    const list = params.map((p) => (p.type ? `${p.name} (${p.type})` : p.name)).join(', ');
    return `${params.length} ${params.length === 1 ? 'property' : 'properties'} to bind in WinCC: ${list}`;
  }, [renderMetadata]);

  const currentRevIndex = revisions.findIndex((r) => r.jobId === jobId);
  const revTag = revisions.length > 1 && currentRevIndex > 0
    ? `rev ${currentRevIndex} · edited`
    : 'rev 0 · original';

  // Verdict for the revision currently on screen (undefined = not rated yet).
  const currentRating = jobId ? ratings[jobId] : null;

  // Import-mode gate: hydrated / sign-in / 1 credit for everyone.
  const canImport = authReady && !!importFile && !importConverting
    && !accountBlocked && hasCreditsFor(1);

  const importViewBox = useMemo(() => {
    if (!importResult?.svghmi) return null;
    const m = importResult.svghmi.match(/viewBox=["']([^"']+)["']/);
    return m ? m[1] : null;
  }, [importResult]);

  // Preview SVG for the import result: prefer the server-rendered preview_svg (has
  // resolved default values for hmi-bind:fill etc.), fall back to the raw svghmi
  // string for backward compat with older backends that don't send preview_svg yet.
  const importPreviewSvg = importResult?.preview_svg || importResult?.svghmi || null;
  const importParamCount = importResult?.render_metadata?.params?.length ?? 0;
  // Memoized for the same reason as generateRenderMetadataArr above — stable
  // array identity so SvghmiPreview's init effect isn't re-armed by unrelated
  // AiGenerator re-renders while the user is mid-edit in the property controls.
  const importRenderMetadata = useMemo(
    () => (importResult?.render_metadata ? [importResult.render_metadata] : []),
    [importResult],
  );

  // Render function for the import-mode property controls: re-renders the raw
  // svghmi source (which carries the bindings) with new mock param values.
  const importRenderFn = useMemo(() => {
    if (!importResult?.svghmi || !token) return null;
    return async (mockValues) => renderSvghmiContent(importResult.svghmi, mockValues, token);
  }, [importResult, token]);

  // Finished widgets plus jobs still waiting for a variant to be chosen.
  // One card per widget (edits folded under their original — see
  // groupJobLineages), only finished ones and those waiting for a pick.
  const completedHistory = useMemo(
    () => groupJobLineages(history.filter((j) => j.status === 'completed' || isChoosable(j))),
    [history],
  );

  // "Open & edit" from a history card: the widget comes back into the result
  // panel; the URL carries ?job= so a reload (or a shared tab) reopens it.
  const onOpenHistory = (card) => {
    try {
      window.history.replaceState(null, '', `/ai-generator?job=${encodeURIComponent(card.job.job_id)}`);
    } catch { /* cosmetic only */ }
    openJob(card.job.job_id, card.title);
  };

  // Showcase tile: fill the prompt and pick its style — never generates by
  // itself (a generation costs credits). A style the catalogue does not know
  // falls back to Auto.
  const onShowcasePick = (item) => {
    setPrompt(item.prompt);
    const style = styleCatalog.styles.find((st) => st.id === item.style);
    let pickedStyle = style ? style.id : AUTO_STYLE;
    if (item.kind === 'icon' || item.style === ICON_STYLE) pickedStyle = ICON_STYLE;
    setStyleId(pickedStyle);
    const detailOk = !!style && styleCatalog.details.some((d) => d.id === item.detail);
    let detail = style ? style.defaultDetail : null;
    if (detailOk) detail = item.detail;
    setDetailId(detail);
    composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    promptRef.current?.focus({ preventScroll: true });
  };

  const composerTagByMode = {
    describe: `${prompt.length}/1000`,
    import: 'PNG · JPG · WEBP · SVG',
    own: '.svghmi · up to 2 MB',
  };
  const composerTag = composerTagByMode[mode] || '';

  // Sidebar of the result workspace: download + TIA guide, restyle, edit,
  // revisions, rating. Rendered beside the preview (under the property
  // controls) through SvghmiPreview's `aside`, or next to the "preview
  // unavailable" canvas when there is no render.
  // Thumbnails of the lineage's revisions (shared by the chat and the
  // fallback refine box).
  const revisionStrip = (
    <div className="ag-revs ag-revs-wrap">
      {revisions.map((rev, i) => {
        const active = rev.jobId === jobId;
        return (
          <button
            key={rev.jobId}
            type="button"
            className={`ag-rev${active ? ' on' : ''}`}
            onClick={() => onSelectRevision(rev)}
            title={rev.label}
          >
            {rev.svg ? (
              <span dangerouslySetInnerHTML={{ __html: rev.svg }} />
            ) : (
              <span className="ag-mono" style={{ fontSize: 10, color: 'var(--ag-faint)' }}>{`v${i}`}</span>
            )}
            <span className="rl">{revisionTag(rev, i)}</span>
          </button>
        );
      })}
    </div>
  );

  const resultSidebar = result ? (
    <div className="ag-side">
      {!generating && (
        <div className="ag-actionbar">
          <button type="button" className="ag-btn ag-btn-ok" onClick={onDownload}>
            Download .svghmi
          </button>
          <WinccGuide guideRef={guideRef} />
        </div>
      )}

      {showRestyle && (
        <RestyleBar
          catalog={styleCatalog}
          result={result}
          busy={restyleBusy || generating || chat.busy}
          error={restyleError}
          onRestyle={onRestyle}
          price={restyleIsImport ? pricing.generation : pricing.restyle}
          note={restyleIsImport
            ? 'Redraws your equipment from parts in the new style — charged like a generation.'
            : null}
        />
      )}

      {!isSampleResult && chatOn && (
        <div className="ag-refine">
          <EditChatPanel
            chat={{ ...chat, send: onSendChat }}
            currentJobId={jobId}
            chips={chatChips}
            priceLabel={chatPrice.label}
            priceCost={chatPrice.cost}
            disabled={!isLoggedIn || generating || accountBlocked}
            disabledReason={accountBlocked ? 'Editing with AI is unlocked by a credit purchase' : null}
            onRestore={onRestoreChatVersion}
            restoreDisabled={generating}
            onBuy={() => showCheckout()}
          />
          {revisions.length > 1 && <div className="sub">{revisionStrip}</div>}
        </div>
      )}

      {/* No edit-chat endpoint on this backend (404): the one-shot refine box. */}
      {(isSampleResult || !chatOn) && (
        <div className="ag-refine">
          {!isSampleResult && (
            <div className="rrow">
              <input
                type="text"
                value={refineInstruction}
                onChange={(e) => setRefineInstruction(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canRefine) onRefine(); }}
                placeholder="Describe a change — e.g. make the needle red, range 0–200"
                maxLength={500}
                disabled={generating}
                aria-label="Describe a change"
              />
              <button
                type="button"
                className="ag-btn ag-btn-primary"
                style={{ padding: '10px 14px' }}
                onClick={onRefine}
                disabled={!canRefine}
              >
                {generating && busyKind === 'refine' ? 'Applying…' : 'Apply edit'}
                {!nextRefineFree && <span className="cr">{formatCredits(nextRefine.price)}</span>}
              </button>
            </div>
          )}
          {/* The edit's progress right where the user pressed the button. */}
          {!isSampleResult && generating && busyKind === 'refine' && (
            <div className="ag-refine-busy" aria-live="polite">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              <span className="ag-refine-what">{`“${pendingInstruction}”`}</span>
              <span className="ag-mono">
                {`${isTakingLonger(elapsedMs, estimate)
                  ? 'Taking a bit longer than usual — still working'
                  : (statusMessage || 'Applying your edit…')} · ${formatClock(elapsedMs)}`}
              </span>
            </div>
          )}

          {!isSampleResult && (
            <div className="sub">
              {revisionStrip}
              <span className={`ag-free${nextRefineFree ? '' : ' pay'}`}>
                {nextRefineFree && (
                  <>
                    <b>{freeEditsLeft}</b>
                    {` of ${freeRefines} free edits left`}
                  </>
                )}
                {!nextRefineFree && (nextRefine.importFirst
                  ? `First edit of your own widget: ${formatCredits(nextRefine.price)}, then ${freeRefines} free`
                  : `Next edit: ${formatCredits(nextRefine.price)}`)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Human verdict — the only line in the product where a live engineer
          says whether the widget was right. Not offered on a sample: the user
          did not commission that widget. (No automated-review line, owner
          2026-10-12: a machine grade read as a verdict on a valid widget.) */}
      {!isSampleResult && (
        <div className="ag-rate">
          <div className="ag-rate-row">
            <span className="ag-rate-q">
              {currentRating ? 'Thanks — noted.' : 'Does this widget look right?'}
            </span>
            <button
              type="button"
              className={`ag-rate-thumb${currentRating?.rating === 1 ? ' on up' : ''}`}
              onClick={onRateUp}
              disabled={ratingBusy}
              aria-pressed={currentRating?.rating === 1}
              aria-label="Good widget"
              title="Good"
            >
              <ThumbIcon />
              Yes
            </button>
            <button
              type="button"
              className={`ag-rate-thumb${currentRating?.rating === -1 ? ' on down' : ''}`}
              onClick={onRateDown}
              disabled={ratingBusy}
              aria-pressed={currentRating?.rating === -1}
              aria-label="Bad widget"
              title="Not good"
            >
              <ThumbIcon down />
              No
            </button>
            {currentRating && (
              <span className="ag-rate-hint">You can change this anytime.</span>
            )}
          </div>

          {currentRating?.rating === -1 && ratingWhyOpen && (
            <div className="ag-rate-why">
              <span className="ag-rate-hint">What went wrong? (optional)</span>
              <div className="ag-rate-chips">
                {RATING_REASONS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`ag-chip${
                      currentRating.reason === item.label
                    || (item.key === 'other' && ratingOtherOpen)
                        ? ' on'
                        : ''
                    }`}
                    onClick={() => onPickReason(item)}
                    disabled={ratingBusy}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              {ratingOtherOpen && (
                <div className="ag-rate-other">
                  <input
                    type="text"
                    value={ratingOtherText}
                    onChange={(e) => setRatingOtherText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onSendOtherReason();
                    }}
                    placeholder="In a few words — what is wrong with it?"
                    maxLength={500}
                    aria-label="Rating reason"
                  />
                  <button
                    type="button"
                    className="ag-btn ag-btn-ghost"
                    style={{ padding: '8px 12px', fontSize: 12 }}
                    onClick={onSendOtherReason}
                    disabled={ratingBusy || !ratingOtherText.trim()}
                  >
                    Send
                  </button>
                </div>
              )}
            </div>
          )}

          {ratingError && (
            <div className="ag-rate-err">{ratingError}</div>
          )}
        </div>
      )}

      <div className="ag-actions">
        {showMoreVariants && (
          <button
            type="button"
            className="ag-btn ag-btn-ghost"
            onClick={onMoreVariants}
            title="Draws more takes on the same prompt; you pick the one to keep"
          >
            {`Show ${extraVariants} more variants`}
            <span className="ag-free"><b>{formatCredits(pricing.more_variants)}</b></span>
          </button>
        )}
        {!isSampleResult && (
          <button
            type="button"
            className="ag-btn ag-btn-ghost"
            onClick={onRegenerate}
            disabled={generating || !hasCreditsFor(genPrice)}
            title="A new generation of the same prompt"
          >
            {`New generation · ${generationPriceLabel}`}
          </button>
        )}
        <button
          type="button"
          className="ag-btn ag-btn-ghost"
          onClick={onReset}
          title="Clears the prompt, preview and revision history — does not start a generation"
        >
          Start over
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="aigen">
      {/* ======================= HERO — composer, style, workspace ======================= */}
      {/* Laid out like an image-generation service (owner, 2026-10-12): a
          full-width composer, the style as picture cards under it, and a
          full-width result workspace below — preview large on the left, the
          property panel / download / restyle / edit in a sidebar. */}
      <section className="ag-hero container ag-wrap">
        <div className="ag-hero-head">
          <span className="ag-eyebrow">AI Widget Generator</span>
          <h1 className="ag-h1">
            Describe a widget. Get a
            {' '}
            <span className="ag-grad">WinCC-ready</span>
            {' '}
            SVGHMI file.
          </h1>
          <p>
            Plain words in, a validated dynamic widget for WinCC Unified out — then
            refine it by talking to it.
          </p>
        </div>

        {/* ---------------- COMPOSER ---------------- */}
        <div className="ag-panel ag-composer" ref={composerRef}>
          <div className="ag-composer-h">
            {/* Mode tabs — Describe (text→widget) | Import image (raster/SVG→widget) */}
            <div className="ag-tabs" role="group" aria-label="Generator mode">
              <button
                type="button"
                className={mode === 'describe' ? 'on' : ''}
                onClick={() => setMode('describe')}
                aria-pressed={mode === 'describe'}
              >
                Describe
              </button>
              <button
                type="button"
                className={mode === 'import' ? 'on' : ''}
                onClick={() => setMode('import')}
                aria-pressed={mode === 'import'}
              >
                Import image
              </button>
              <button
                type="button"
                className={mode === 'own' ? 'on' : ''}
                onClick={() => setMode('own')}
                aria-pressed={mode === 'own'}
              >
                Edit my SVGHMI
              </button>
            </div>
            <span className="tag ag-mono">{composerTag}</span>
          </div>

          {mode === 'describe' && (
            <>
              <div className="ag-compose">
                <div className="ag-promptbox">
                  {!prompt && (
                    <div className="ag-ph" aria-hidden="true">
                      <span>{typedPlaceholder}</span>
                      <span className="ag-cursor" />
                    </div>
                  )}
                  <textarea
                    ref={promptRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onFocus={onPromptFocus}
                    onKeyDown={(e) => {
                      // Ctrl/Cmd+Enter generates, like the chat composers.
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canGenerate) onGenerate();
                    }}
                    maxLength={1000}
                    disabled={generating}
                    aria-label="Widget prompt"
                    placeholder="Describe your widget…"
                  />
                </div>
                {promptHint(iconMode ? 'icon' : 'widget', prompt) && (
                  <div className="ag-prompt-hint" aria-live="polite">
                    {promptHint(iconMode ? 'icon' : 'widget', prompt)}
                  </div>
                )}
                {/* The style cards are the one choice under the prompt. */}
                <StylePicker
                  catalog={styleCatalog}
                  styleId={styleId}
                  detailId={detailId}
                  onStyle={onPickStyle}
                  onDetail={setDetailId}
                  disabled={generating}
                />
              </div>
              <div className="ag-compose-foot">
                {/* Creativity — always visible, left of Generate (owner, 2026-10-12:
                    nothing hidden behind "More options"). */}
                <div className="ag-creativity">
                  <span className="ag-style-k" id="ag-creativity-k">Creativity</span>
                  <div className="ag-seg" role="group" aria-labelledby="ag-creativity-k">
                    {CREATIVITY_LEVELS.map(({
                      key, label, color, hint,
                    }) => (
                      <button
                        key={key}
                        type="button"
                        className={creativity === key ? 'on' : ''}
                        onClick={() => setCreativity(key)}
                        disabled={generating}
                        aria-pressed={creativity === key}
                        title={hint}
                      >
                        <span className="d" style={{ background: color }} />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <span className="ag-grow" />
                <button
                  type="button"
                  className="ag-btn ag-btn-primary"
                  onClick={onGenerate}
                  disabled={!canGenerate}
                >
                  {generating && busyKind === 'generate' ? 'Generating…' : 'Generate'}
                  {!generating && <span className="cr">{generationPriceLabel}</span>}
                  {!generating && <PriceBadge text={pricing.badge} onDark />}
                </button>
              </div>

              {/* credits / gates — all gated on `hydrated` (see hydration note above) */}
              {authReady && !accountBlocked && (
                <div className="ag-credits-line">
                  <b className={hasCreditsFor(genPrice) ? 'ag-credits-ok' : 'ag-credits-low'}>
                    {balance ?? 0}
                  </b>
                  {' '}
                  {`credits available · each generation costs ${genPrice}`}
                  {signupExpiryLabel && ` · free credits expire on ${signupExpiryLabel}`}
                  {' '}
                  <PriceBadge text={pricing.badge} />
                </div>
              )}

              {(!hydrated || !isLoggedIn) && (
                // Guests: the sign-up credits are the pitch. Same wording as
                // /pricing and the FAQ (utils/aiPricing.js).
                <div className="ag-note ag-note-info ag-guest-cta">
                  <a href="/auth-signup?from=/ai-generator" className="ag-btn ag-btn-primary">
                    Sign up free
                  </a>
                  <span>
                    <b>{signupOfferText()}</b>
                    {` — ${signupCreditsReach(pricing.generation)}. Already have an account? `}
                    <a href="/auth-login?from=/ai-generator">Sign in</a>
                  </span>
                </div>
              )}
              {authReady && accountBlocked && (
                <div className="ag-note ag-note-warn">
                  AI generation on this account is unlocked by a credit purchase.
                  {' '}
                  <BuyNowLink checkoutUrl={checkoutUrl} onShow={showCheckout} className="" label="Buy credits" />
                  {' '}
                  to start creating widgets.
                </div>
              )}
              {authReady && !accountBlocked && !hasCreditsFor(genPrice) && (
                <div className="ag-note ag-note-warn">
                  Out of credits.
                  {' '}
                  <BuyNowLink checkoutUrl={checkoutUrl} onShow={showCheckout} className="" label="Buy credits" />
                  {' '}
                  to keep generating.
                </div>
              )}
              {error && (
                <div
                  className="ag-note ag-note-alarm"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  }}
                >
                  <span>{error.message}</span>
                  {error.credits_refunded && <span>(credits refunded)</span>}
                  {error.buy && (
                    <BuyNowLink checkoutUrl={checkoutUrl} onShow={showCheckout} className="" label="Buy credits" />
                  )}
                  <button
                    type="button"
                    className="ag-btn ag-btn-ghost"
                    style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: 13 }}
                    onClick={onReset}
                  >
                    Try again
                  </button>
                </div>
              )}
            </>
          )}

          {mode === 'own' && (
            <>
              <div className="ag-compose">
                <div
                  className={`ag-drop${ownDragOver ? ' over' : ''}${ownFile ? ' has-file' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => !ownBusy && ownInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (!ownBusy) ownInputRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (!ownBusy) setOwnDragOver(true);
                  }}
                  onDragLeave={() => setOwnDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setOwnDragOver(false);
                    if (!ownBusy) selectOwnFile(e.dataTransfer?.files?.[0]);
                  }}
                  aria-label="Upload your own .svghmi widget to edit it"
                >
                  <input
                    ref={ownInputRef}
                    type="file"
                    className="ag-drop-input"
                    accept={OWN_ACCEPT}
                    disabled={ownBusy}
                    onChange={(e) => selectOwnFile(e.target.files?.[0])}
                  />
                  {ownFile ? (
                    <div className="ag-drop-file">
                      <span className="ag-drop-ico" aria-hidden="true">⬡</span>
                      <div className="ag-drop-meta">
                        <div className="ag-drop-name">{ownFile.name}</div>
                        <div className="ag-drop-size ag-mono">{formatBytes(ownFile.size)}</div>
                      </div>
                      <button
                        type="button"
                        className="ag-btn ag-btn-ghost"
                        style={{ padding: '6px 12px', fontSize: 12 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOwnFile(null);
                          if (ownInputRef.current) ownInputRef.current.value = '';
                        }}
                        disabled={ownBusy}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="ag-drop-empty">
                      <span className="ag-drop-ico" aria-hidden="true">⬆</span>
                      <div className="ag-drop-t">Drop your .svghmi widget here</div>
                      <div className="ag-drop-p ag-mono">or click to browse · your own widget · up to 2 MB</div>
                    </div>
                  )}
                </div>
                <label className="ag-own-desc">
                  <span className="ag-style-k">What does it show? (optional)</span>
                  <input
                    type="text"
                    value={ownDescription}
                    onChange={(e) => setOwnDescription(e.target.value)}
                    maxLength={300}
                    placeholder="e.g. centrifugal pump with motor — helps Restyle recognise it"
                    disabled={ownBusy}
                  />
                </label>
              </div>
              <div className="ag-compose-foot">
                <span className="ag-import-note">
                  Opens your widget with its properties. Then describe an edit or pick a new style.
                </span>
                <span className="ag-grow" />
                <button
                  type="button"
                  className="ag-btn ag-btn-primary"
                  onClick={onOpenOwn}
                  disabled={!ownFile || ownBusy || generating}
                >
                  {ownBusy ? 'Opening…' : 'Open for editing'}
                  <span className="cr">free</span>
                </button>
              </div>
              {(!hydrated || !isLoggedIn) && (
                <div className="ag-note ag-note-warn">
                  <a href="/auth-login?from=/ai-generator">Sign in</a>
                  {' '}
                  to open and edit your widgets.
                </div>
              )}
              {ownError && (
                <div className="ag-note ag-note-alarm" role="alert">{ownError}</div>
              )}
            </>
          )}

          {mode === 'import' && (
            <>
              <div className="ag-compose">
                <div
                  className={`ag-drop${importDragOver ? ' over' : ''}${importFile ? ' has-file' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => !importConverting && importInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (!importConverting) importInputRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (!importConverting) setImportDragOver(true);
                  }}
                  onDragLeave={() => setImportDragOver(false)}
                  onDrop={onImportDrop}
                  aria-label="Upload an image or SVG to convert to an SVGHMI widget"
                >
                  <input
                    ref={importInputRef}
                    type="file"
                    className="ag-drop-input"
                    accept={IMPORT_ACCEPT}
                    disabled={importConverting}
                    onChange={(e) => selectImportFile(e.target.files?.[0])}
                  />
                  {importFile ? (
                    <div className="ag-drop-file">
                      {importPreview ? (
                      // eslint-disable-next-line @next/next/no-img-element
                        <img className="ag-drop-thumb" src={importPreview} alt="Selected file preview" />
                      ) : (
                        <span className="ag-drop-ico" aria-hidden="true">⬡</span>
                      )}
                      <div className="ag-drop-meta">
                        <div className="ag-drop-name">{importFile.name}</div>
                        <div className="ag-drop-size ag-mono">{formatBytes(importFile.size)}</div>
                      </div>
                      <button
                        type="button"
                        className="ag-btn ag-btn-ghost"
                        style={{ padding: '6px 12px', fontSize: 12 }}
                        onClick={(e) => { e.stopPropagation(); onImportReset(); }}
                        disabled={importConverting}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="ag-drop-empty">
                      <span className="ag-drop-ico" aria-hidden="true">⬆</span>
                      <div className="ag-drop-t">Drag &amp; drop an image or SVG</div>
                      <div className="ag-drop-p ag-mono">or click to browse · PNG, JPG, WEBP, SVG · up to 9 MB</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="ag-compose-foot">
                <span className="ag-import-note">
                  Imported widgets are static.
                  {' '}
                  <button type="button" className="ag-linkbtn" onClick={() => setMode('describe')}>
                    Describe one instead
                  </button>
                  {' '}
                  to get an interactive widget with bindings.
                </span>
                <span className="ag-grow" />
                <button
                  type="button"
                  className="ag-btn ag-btn-primary"
                  onClick={onConvertImport}
                  disabled={!canImport}
                >
                  {importConverting ? 'Vectorizing…' : 'Convert to widget'}
                  <span className="cr">
                    1 credit
                  </span>
                </button>
              </div>

              {/* credits / gates — all gated on `hydrated` (see hydration note
                  above). An import costs 1 credit for everyone. */}
              {authReady && !accountBlocked && (
                <div className="ag-credits-line">
                  <b className={hasCreditsFor(1) ? 'ag-credits-ok' : 'ag-credits-low'}>
                    {balance ?? 0}
                  </b>
                  {' '}
                  credits available · each import costs 1
                  {signupExpiryLabel && ` · free credits expire on ${signupExpiryLabel}`}
                </div>
              )}

              {(!hydrated || !isLoggedIn) && (
                <div className="ag-note ag-note-warn">
                  <a href="/auth-login?from=/ai-generator">Sign in</a>
                  {' '}
                  to import and convert images.
                </div>
              )}
              {authReady && accountBlocked && (
                <div className="ag-note ag-note-warn">
                  AI generation on this account is unlocked by a credit purchase.
                  {' '}
                  <BuyNowLink checkoutUrl={checkoutUrl} onShow={showCheckout} className="" label="Buy credits" />
                  {' '}
                  to start importing.
                </div>
              )}
              {authReady && !accountBlocked && !hasCreditsFor(1) && (
                <div className="ag-note ag-note-warn">
                  Importing an image costs 1 credit, and you have none left.
                  {' '}
                  <BuyNowLink checkoutUrl={checkoutUrl} onShow={showCheckout} className="" label="Buy credits" />
                </div>
              )}
              {importError && (
                <div
                  className="ag-note ag-note-alarm"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  }}
                >
                  <span>{importError}</span>
                  {importErrorBuy && (
                    <BuyNowLink checkoutUrl={checkoutUrl} onShow={showCheckout} className="" label="Buy credits" />
                  )}
                  <button
                    type="button"
                    className="ag-btn ag-btn-ghost"
                    style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: 13 }}
                    onClick={() => setImportError(null)}
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ---------------- WORKSPACE (describe / own file) ---------------- */}
        {(mode === 'describe' || mode === 'own') && (
          <div ref={resultPanelRef} className="ag-ws">
            {showVariants && (
              <div className="ag-panel">
                <div className="ag-panel-h">
                  <span className="k">Pick a variant</span>
                  <span className="tag ag-mono">{`${variantsJob.variants.length} variants`}</span>
                </div>
                <VariantsGrid
                  variants={variantsJob.variants}
                  catalog={styleCatalog}
                  token={token}
                  choosingIdx={choosingIdx}
                  onChoose={onChooseVariant}
                  expiresAt={variantsJob.expiresAt}
                  error={variantsError}
                  disabled={generating}
                />
              </div>
            )}

            {!showVariants && result && (
              <>
                <div className="ag-panel">
                  <div className="ag-panel-h">
                    <span className="k">Result</span>
                    <span className="tag ag-mono">{revTag}</span>
                  </div>
                  {/* A generation/refine running on top of a widget that is already
                      on screen: the widget stays where it is (dimmed) and the
                      progress readout takes over the metadata strip's slot. */}
                  {generating ? (
                    <div className="ag-gp-dock">
                      <GenerationProgress
                        elapsedMs={elapsedMs}
                        status={status}
                        statusMessage={statusMessage}
                        queuePosition={queuePosition}
                        isRetrying={isRetrying}
                        estimate={estimate}
                        compact
                      />
                    </div>
                  ) : (
                    <div className="ag-result-head">
                      <span className={`badge2${result.validation?.valid ? '' : ' warn'}`}>
                        <span className="ag-dot" />
                        {result.validation?.valid ? 'validated · renders in WinCC' : 'auto-fixed · renders in WinCC'}
                        {result.validation?.errors_fixed > 0 ? ` (${result.validation.errors_fixed} fixed)` : ''}
                      </span>
                      <span className="meta2 ag-bindable" title={bindableLine}>
                        {bindableLine}
                      </span>
                    </div>
                  )}

                  {/* Said plainly and above the fold. Someone who believes
                      this widget was built for them, from their credits,
                      will feel cheated the moment they find out otherwise. */}
                  {isSampleResult && (
                    <div className="ag-sample-note">
                      <b>This is a free demo.</b>
                      {` Change anything and press Generate (${formatCredits(genPrice)}) to get your own.`}
                    </div>
                  )}

                  {svgPreview ? (
                    <div className={`ag-editor-host ag-fadeup${generating ? ' is-busy' : ''}`}>
                      <SvghmiPreview
                        optimizeId={jobId}
                        renderedSvgs={[svgPreview]}
                        renderMetadata={generateRenderMetadataArr}
                        renderFn={aiRenderFn}
                        editorEnabled
                        sourceXml={sourceXml}
                        keepParamValuesOnSameNames
                        aside={resultSidebar}
                      />
                    </div>
                  ) : (
                    <div className="ag-ws-grid">
                      <div className="ag-canvas">
                        <div className="ag-progress">
                          <div className="msg">Preview unavailable — you can still download the file.</div>
                        </div>
                      </div>
                      {resultSidebar}
                    </div>
                  )}
                </div>
                {/* Coilmind pitch once the user has a real widget of
                    their own (not on the free demo). */}
                {!isSampleResult && !generating && <CoilmindCard />}
              </>
            )}

            {!showVariants && !result && generating && (
              <div className="ag-panel">
                <div className="ag-canvas ag-canvas-wait">
                  <div className="ag-progress">
                    <div className="ag-gp-orb" aria-hidden="true">
                      <span className="ag-gp-orb-ring" />
                      <span className="ag-gp-orb-core" />
                    </div>
                    <GenerationProgress
                      elapsedMs={elapsedMs}
                      status={status}
                      statusMessage={statusMessage}
                      queuePosition={queuePosition}
                      isRetrying={isRetrying}
                      estimate={estimate}
                    />
                  </div>
                </div>
              </div>
            )}

            {mode === 'own' && !showVariants && !result && !generating && (
              <div className="ag-panel">
                <div className="ag-canvas">
                  <div className="ag-empty">
                    <div className="ag-drop-ico" style={{ fontSize: 40 }} aria-hidden="true">⬡</div>
                    <div className="cap">Your widget opens here — with its properties, ready to edit</div>
                  </div>
                </div>
              </div>
            )}

            {mode === 'describe' && !showVariants && !result && !generating && (
              showcase.length > 0 ? (
                <>
                  <ShowcaseHero />
                  <Showcase items={showcase} onPick={onShowcasePick} disabled={generating} />
                </>
              ) : (
                // No showcase manifest yet: the one shipped sample widget.
                <div className="ag-panel">
                  <div className="ag-canvas">
                    <div className="ag-empty">
                      <div className="ag-sample">
                        <span className="ag-sample-badge">
                          <span className="ag-dot" />
                          validated · renders in WinCC
                        </span>
                        {/* Real rendered sample widget — not a live result. */}
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          className="ag-sample-img"
                          src="/ai-examples/chlorine-monitor.png"
                          alt="Example generated widget"
                          loading="lazy"
                        />
                        <span className="ag-sample-tag">example</span>
                      </div>
                      <div className="cap">Example — describe yours to generate</div>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        )}

        {/* ---------------- WORKSPACE (import) ---------------- */}
        {mode === 'import' && (
          <div className="ag-ws">
            <div className="ag-panel">
              <div className="ag-panel-h">
                <span className="k">Result</span>
                {importResult && (
                  <span className="tag ag-mono">
                    {importParamCount > 0 ? 'vectorized' : 'vectorized · static'}
                  </span>
                )}
              </div>

              {importResult ? (
                <>
                  <div className="ag-result-head">
                    <span className="badge2">
                      <span className="ag-dot" />
                      vectorized · renders in WinCC
                    </span>
                    <span className="meta2">
                      {importViewBox ? `viewBox ${importViewBox}` : 'svghmi'}
                      {' · '}
                      {importResult.stats?.paths ?? 0}
                      {' paths · '}
                      {formatBytes(importResult.stats?.bytes)}
                    </span>
                  </div>

                  {importResult.stats?.heavy && (
                    <div className="ag-note ag-note-warn" style={{ margin: '0 16px 12px' }}>
                      {importResult.stats.warning
                      || 'Heavy for WinCC performance class — consider simplifying.'}
                    </div>
                  )}

                  <div className="ag-editor-host ag-fadeup">
                    <SvghmiPreview
                      renderedSvgs={[importPreviewSvg]}
                      renderMetadata={importRenderMetadata}
                      renderFn={importRenderFn}
                      fileName={importResult.fileName}
                    />
                  </div>

                  <div className="ag-actions ag-actions-pad">
                    <button type="button" className="ag-btn ag-btn-ok" onClick={onImportDownload}>
                      Download .svghmi
                    </button>
                    <button type="button" className="ag-btn ag-btn-ghost" onClick={onImportReset}>
                      Convert another
                    </button>
                  </div>
                </>
              ) : (
                <div className="ag-canvas">
                  {importConverting ? (
                    <div className="ag-progress">
                      <div className="ag-gp-orb" aria-hidden="true">
                        <span className="ag-gp-orb-ring" />
                        <span className="ag-gp-orb-core" />
                      </div>
                      <div className="ag-gp">
                        <div className="ag-gp-head">
                          <span className="ag-gp-msg">Vectorizing your image…</span>
                          <span className="ag-gp-nums ag-mono">
                            <span className="ag-gp-clock">{formatClock(importElapsedMs)}</span>
                          </span>
                        </div>
                        <div className="ag-gp-track" role="progressbar" aria-label="Vectorizing">
                          <div className="ag-gp-fill indet" />
                        </div>
                        <div className="ag-gp-sub">
                          Tracing paths and converting to SVGHMI.
                        </div>
                        <div className="ag-gp-sub ag-gp-cheer">
                          {waitingPhraseAt(importElapsedMs)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="ag-empty">
                      {importPreview ? (
                        <div className="ag-sample">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img className="ag-sample-img" src={importPreview} alt="Selected source" />
                          <span className="ag-sample-tag">source</span>
                        </div>
                      ) : (
                        <div className="ag-drop-ico" style={{ fontSize: 40 }} aria-hidden="true">⬡</div>
                      )}
                      <div className="cap">
                        {importFile ? 'Ready — convert to a widget' : 'Upload an image to vectorize'}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ======================= HOW IT WORKS ======================= */}
      <section className="ag-blk container ag-wrap">
        <div className="ag-sec-head">
          <div>
            <span className="ag-eyebrow">How it works</span>
            <h2 className="ag-h2">Three steps, no drawing tools</h2>
          </div>
        </div>
        <div className="row g-3">
          {HOW_IT_WORKS.map((s) => (
            <div className="col-md-4" key={s.id}>
              {/* id anchors back the HowTo step URLs in the page's structured data */}
              <div className="ag-step" id={s.id}>
                <div className="bar" />
                <div className="n">{s.n}</div>
                <h3 className="ag-h3">{s.title}</h3>
                <p>{s.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ======================= COILMIND ======================= */}
      <CoilmindBand />

      {/* history — lazy-hover previews of the user's recent widgets (hydration-gated).
          Deliberately NOT gated on `!generating`: unmounting it mid-generation removed a
          whole section from the flow and yanked everything below it up the page. */}
      {authReady && completedHistory.length > 0 && (
        <section className="ag-blk container ag-wrap" ref={historyRef}>
          <div className="ag-sec-head">
            <div>
              <span className="ag-eyebrow">History</span>
              <h2 className="ag-h2">Your last generated widgets</h2>
            </div>
            {historyLoading && <div className="spinner-border spinner-border-sm text-secondary" />}
          </div>
          <div className="row g-3">
            {completedHistory.map((card) => {
              const { job } = card;
              return (
                <div className="col-6 col-md-3" key={job.job_id}>
                  <div className="ag-hist-card">
                    <div className="ag-hist-thumb">
                      {job.status === 'completed' ? (
                        <HistoryThumb
                          jobId={job.job_id}
                          preview={historyPreviews[job.job_id]}
                          onVisible={loadHistoryPreview}
                        />
                      ) : (
                        <span className="ag-mono" style={{ fontSize: 12, color: 'var(--ag-accent-ink)' }}>
                          Variants ready — pick one
                        </span>
                      )}
                    </div>
                    <div className="ag-hist-body">
                      <div className="ag-hist-prompt" title={card.title}>{card.title}</div>
                      <div className="ag-hist-date">
                        {new Date(job.created_at * 1000).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                        {card.edits > 0 && ` · ${card.edits} ${card.edits === 1 ? 'edit' : 'edits'}`}
                      </div>
                      <div className="ag-hist-foot">
                        {job.status === 'completed' ? (
                          <>
                            <button
                              type="button"
                              className="ag-btn ag-btn-primary"
                              style={{ padding: '5px 10px', fontSize: 12 }}
                              onClick={() => onOpenHistory(card)}
                              disabled={generating}
                            >
                              Open &amp; edit
                            </button>
                            <button
                              type="button"
                              className="ag-btn ag-btn-ghost"
                              style={{ padding: '5px 10px', fontSize: 12 }}
                              onClick={() => downloadHistoryItem(
                                job.file_url,
                                job.job_id,
                                card.title,
                              )}
                            >
                              Download
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="ag-btn ag-btn-primary"
                            style={{ padding: '5px 12px', fontSize: 12 }}
                            onClick={() => onOpenHistory(card)}
                            disabled={generating}
                          >
                            Pick a variant
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ======================= WHY ======================= */}
      <section className="ag-blk container ag-wrap">
        <div className="row g-3">
          {[
            { ic: '◎', h: 'Geometry that doesn’t drift', p: 'Scales, needles and arcs are composed in code — not guessed by the model — so gauges are correct every time.' },
            { ic: '✓', h: 'Opens in WinCC Unified', p: 'Every file passes structural validation and renders before you download. No broken widgets.' },
            { ic: '⟳', h: 'Edit, don’t regenerate', p: 'Follow-ups change the widget you have. No more losing a good result to a fresh roll of the dice.' },
          ].map((w) => (
            <div className="col-md-4" key={w.h}>
              <div className="ag-why">
                <div className="h">
                  <span className="ic">{w.ic}</span>
                  {w.h}
                </div>
                <p>{w.p}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ======================= FAQ ======================= */}
      {/* Native <details>: the answers sit in the HTML whether or not a panel is
          open, which is what FAQPage structured data (and any answer engine
          reading the page) needs — a JS accordion that mounts content on click
          would leave the markup empty. No client state, no hydration cost. */}
      <section className="ag-blk container ag-wrap" id="faq">
        <div className="ag-sec-head">
          <div>
            <span className="ag-eyebrow">FAQ</span>
            <h2 className="ag-h2">Questions engineers actually ask</h2>
            <p>
              Still stuck? Write to
              {' '}
              <a href="mailto:support@svghmi.pro">support@svghmi.pro</a>
              {' '}
              — a human answers.
            </p>
          </div>
        </div>
        <div className="ag-faq">
          {FAQ.map((item) => (
            <details className="ag-faq-item" key={item.id} id={item.id}>
              <summary>
                <span className="q">{item.q}</span>
                <span className="ag-faq-ico" aria-hidden="true" />
              </summary>
              <div className="ag-faq-a">{item.a}</div>
            </details>
          ))}
        </div>
      </section>

      {toast === 'saved' && (
        <div className="ag-toast" role="status">
          <span>Saved. Next: add it to your TIA Portal project →</span>
          <button type="button" className="ag-linkbtn" onClick={openGuide}>guide</button>
          <button type="button" className="ag-toast-x" onClick={() => setToast(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* ======================= PRICING ======================= */}
      {/* Every figure from GET /quota → pricing (normalizeAiPricing
          fallbacks until it answers); the packs are the
          live public credit packs (GET /products/credits), as on /pricing. */}
      <section className="ag-blk container ag-wrap" id="pricing-section">
        <div className="ag-sec-head">
          <div>
            <span className="ag-eyebrow">Pricing</span>
            <h2 className="ag-h2">Pay per widget you keep</h2>
            <p>
              Charged when a generation starts, refunded automatically if it fails.
              {' '}
              <PriceBadge text={pricing.badge} />
            </p>
          </div>
        </div>
        <ul className="ag-tariff">
          {tariffRows.map((row) => (
            <li key={row.what}>
              <span className="what">
                {row.what}
                {row.note && <span className="note">{row.note}</span>}
              </span>
              <span className="cost">{row.cost}</span>
            </li>
          ))}
        </ul>
        {packs.length > 0 && (
          <div className="row g-3 mt-1">
            {packs.map((tier, i) => {
              const best = i === bestPackIndex;
              return (
                <div className={packColClass} key={tier.id ?? tier.credits}>
                  <div className={`ag-plan${best ? ' feat' : ''}`}>
                    {best && <span className="ribbon">BEST VALUE</span>}
                    <span className="nm">{packTitle(tier, i)}</span>
                    <span className="cr">{`${tier.credits} Credits`}</span>
                    <span className="pr">
                      €
                      {tier.price}
                    </span>
                    <span className="per">
                      {formatPricePerCredit(tier, ' per ')}
                    </span>
                    <ul>
                      {planFeatures(pricing, tier).map((f) => (
                        <li key={f}>
                          <span className="ck">✓</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <div className="buy-wrap">
                      <PricingBuyButton item={tier} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <CoilmindPricingLine />
      </section>
    </div>
  );
}
